/* eslint-disable no-case-declarations */
/* eslint-disable no-console */
/* eslint-disable no-underscore-dangle */
/* eslint-disable import/no-cycle */
import getTime from 'date-fns/getTime';
import compareVersions from 'semver-compare';
import {
  Strophe, $iq, $msg, $pres,
} from 'strophe.js';
import uuidv1 from 'uuid/v4';
import TabEmitter from 'tab-emitter';
import EventEmitter from 'eventemitter3';
import { LogLevel } from '@bugfender/sdk';
import * as Logger from './logger';
import { safeSetMediaPlaybackState } from './media-session';
import { generateJID } from './index';
import { webSocketHost } from '../api';

import { Enum } from './enum.es6';

import {
  processServerMsg, processChatMsg, processCallMsg,
  createPeerConnection, sendAnswerData, onIceCandidate,
  encodeXMPPmessage, getMediaConstraints, sendInCallAnswerData, checkUserAndGet, trackStop, isCallRequestMsg,
  redactChatMessageBody,
} from './con-helpers';

import { getCallDetails } from '../api/pros';

import {
  consts,
  timeouts,
  statuses,
  USER_AUTH,
  DISCONNECT_TIMEOUT_MS,
  RECONNECT_TIMEOUT_MS,
  VIDEO_PLAYING_TIMEOUT_MS,
  ENVIRONMENTS,
} from './consts';

import 'strophejs-plugin-stream-management';

import PeerConnectionQualityMonitor from './connection-quality-monitoring';

const WEBRTC_STUN_PATH = process.env.PREACT_APP_WEBRTC_STUN_PATH;
const WEBRTC_TURN_PATH = process.env.PREACT_APP_WEBRTC_TURN_PATH;
const TURN_USERNAME = process.env.PREACT_APP_TURN_USERNAME;
const TURN_PASSWORD = process.env.PREACT_APP_TURN_PASSWORD;
const XMPP_DOMAIN = process.env.PREACT_APP_XMPP_DOMAIN;
const XMPP_PING_INTERVAL_MS = 1000 * 20;
const XMPP_PONG_INTERVAL_MS = 1000 * 10;
const XMPP_MAX_MISSED_PONGS = 3;
const XMPP_ACK_REQUEST_DISABLED = 0;
const XMPP_MAX_UNACKNOWLEDGED_STANZAS = 5;
const XMPP_MAX_RETRANSMIT_ATTEMPTS = 2;
const WEBRTC_ICE_RESTART_DELAY_AFTER_DISCONNECTED = 10000;
const CROSS_TAB_RINGING_EVENT = 'ringing';
export const CALL_FAILED_EVENT = 'callFailed';
export const INCOMING_CALL_STARTED_EVENT = 'incomingCallStarted';
export const INCOMING_CALL_REQUEST_EVENT = 'incomingCallRequest';
export const OUTGOING_CALL_STARTED_EVENT = 'outgoingCallStarted';
export const CALL_FINISHED_EVENT = 'callFinished';
export const CALL_CONNECTED_EVENT = 'callConnected';
export const SCREEN_SHARING_PERMISSION_DENIED_EVENT = 'screenSharingPermissionDenied';
export const RECONNECTION_COMPLETE = 'reconnectionComplete';

const MAX_SUPPORTED_SIGNALING_PROTOCOL = '3.1';
const MIN_SIGNALING_PROTOCOL_SUPPORTING_CALLSATUS_REQUEST = '3.1';
const MIN_SUPPORTED_SIGNALING_PROTOCOL = '3.0';

export const ERR_NO_AUDIO_DEVICE = 'Starting a call requires your system to have at least one working audio input device such as a microphone or a headset';
export const ERR_ACCESS_TO_MEDIA_DEVICE_DENIED = 'Access to the microphone or camera is denied; please check your security settings';

export const CallState = new Enum({
  IDLE: { value: 0, description: '' },
  INVITED: { value: 1, description: 'request' },
  ACCEPTED: { value: 2, description: 'accepted' },
  ACTIVE: { value: 3, description: 'speaking' },
  SUCCEEDED: { value: 4, description: 'finished' },
  BREAK: { value: 5, description: 'avTimeEnded | bookingEnded' },
  PRO_BUSY: { value: 6, description: 'proBusy' },
  NO_ANSWER: { value: 7, description: 'noAnswer' },
  CLIENT_DECLINE: { value: 8, description: 'clientDeclined' },
  PRO_DECLINE: { value: 9, description: 'receiverDeclined' },
  DISCONNECTED: { value: 10, description: 'rtcConnectionLost | callDisconnected' },
  FAILED: { value: 11, description: 'failed' },
  PRO_TIMEOUT: { value: 12, description: 'before sending receiverDeclined on timeout' },
});

const __FILE__ = 'connection.js';

const stropheLogger = (level, msg) => {
  const __METHOD__ = 'Connection::stropheLogger';

  if (level >= Strophe.LogLevel.WARN) {
    Logger.logXMPPClient(__FILE__, __METHOD__, msg);
  }
};

class Connection {
  constructor(props) {
    Strophe.addNamespace('PING', 'urn:xmpp:ping');
    Strophe.addNamespace('DELAY', 'urn:xmpp:delay');

    this._userAuth = '';
    this._curUserId = 0;
    this._curUserJID = '';

    this._calleeId = '';
    this._calleeJID = '';
    this.callInitiatorJID = null;

    this.offsetAutoGetHistory = 0;

    this.props = props;

    this.persons = {};
    this.webRtcPeers = {};
    this.candidates = [];
    this.localStream = null;
    this.mediaConstraints = { video: false, audio: false };
    this.amICallee = false;
    // UGLY HACK for iOS delayed messaging,
    // when offerData and iceCandidates comes after call rejection
    this.callWasRejected = false;
    this.callFailed = false;
    this.connected = false;

    this.initialised = false;

    this.presenceConfirmationGranted = false;
    this.callTimeBeep = timeouts.autoRejectTimeout;

    this.isScreenSharingListener = true;

    this.isAttemptingToConnect = false;
    this.pingTimer = undefined;
    this.pongTimer = undefined;
    this.pingIqId = undefined;
    this.missedPongsCount = 0;
    this.awaitPongResponseArmed = false;
    this.callState = CallState.IDLE;

    this.unacknowledgedStanzas = {};
    this.unacknowledgedStanzaRetryCounts = {};
    this.messageHistoryIQIds = {};

    this.isOutgoingScreenSharingMuted = true;
    this.isOutgoingVideoMuted = true;
    this.isOutgoingAudioMuted = true;

    const __CALLBACK_METHOD__ = 'Connection::peerConnectionQualityMonitorCallback';
    this.connectionQualityMonitor = new PeerConnectionQualityMonitor(
      (qualityChanges) => qualityChanges.forEach((change) => Logger.logWebRTC(
        __FILE__,
        __CALLBACK_METHOD__,
        change,
      )),
    );

    this.events = new EventEmitter();
  }

  setCallTimeBeep = (callTimeBeepSec) => {
    this.callTimeBeep = callTimeBeepSec * 1000;
  };

  initializeConnection = async (props) => {
    const __METHOD__ = 'Connection::initializeConnection';
    const __CALLBACK_METHOD__ = 'Connection::stropheXmlOutputCallback';

    if (!this.isAttemptingToConnect && !this.connected) {
      this.isAttemptingToConnect = true;
      const { userData: user = {} } = props;
      if (Object.keys(user).length === 0) return;

      this.presenceConfirmationGranted = false;

      const { userAuth, id } = user;
      const oldUserId = this._curUserId;
      this._curUserId = id;
      this._curUserJID = generateJID(this._curUserId, false, this.props.uuid);
      this._userAuth = userAuth || localStorage.getItem(USER_AUTH);
      this.mediaConstraints = await getMediaConstraints();

      this.disarmPingPongMonitoring();

      if (oldUserId && oldUserId !== id) {
        this.unacknowledgedStanzas = {};
        this.unacknowledgedStanzaRetryCounts = {};
        this.messageHistoryIQIds = {};
      }

      Strophe.log = stropheLogger;

      if (this.connection) {
        this.connection.reset();
      }

      this.connection = new Strophe.Connection(`wss://${webSocketHost}:5281/websocket`, {});

      this.connection.streamManagement.logging = (process.env.PREACT_APP_ENV === ENVIRONMENTS.DEVELOPMENT);
      this.connection.streamManagement.addAcknowledgedStanzaListener(this.onAcknowledgedStanza);
      this.connection.streamManagement.requestResponseInterval = XMPP_ACK_REQUEST_DISABLED;

      this.connection.connect(this._curUserJID, '', this.onConnect);

      this.oldConnectionXmlOutputHandler = this.connection.xmlOutput;
      this.connection.xmlOutput = (elem) => {
        try {
          if (elem.localName.toLowerCase() === 'iq') {
            if ((elem.childElementCount === 0) && (elem.hasAttribute('type') && elem.getAttribute('type').toLowerCase() === 'result')) {
              // Do not output ping replies
              return;
            }

            if (elem.lastElementChild && elem.lastElementChild.localName === 'ping' && elem.lastElementChild.childElementCount === 0) {
              // Do not output ping requests
              return;
            }
          }

          if (elem.localName.toLowerCase() === 'a') {
            // Do not output stream management ACKs
            return;
          }

          if ((elem.localName.toLowerCase() === 'message') && elem.hasAttribute('type') && (elem.getAttribute('type').toLowerCase() === 'chat')) {
            // Redact chat message body
            Logger.logXMPPSend(
              __FILE__,
              __CALLBACK_METHOD__,
              redactChatMessageBody(elem),
            );
          } else {
            Logger.logXMPPSend(__FILE__, __CALLBACK_METHOD__, `${elem.outerHTML}`);
          }
        } finally {
          if (this.oldConnectionXmlOutputHandler && typeof this.oldConnectionXmlOutputHandler === 'function') {
            this.oldConnectionXmlOutputHandler(elem);
          }
        }
      };

      this._crossTabEmitter && typeof this._crossTabEmitter.removeAllListeners !== 'undefined' && this._crossTabEmitter.removeAllListeners();
      this._crossTabEmitter = TabEmitter(this._curUserId);
      this._crossTabEmitter.on(CROSS_TAB_RINGING_EVENT, this.onRinging.bind(this));
    } else {
      let hasNoEffectMessage;

      if (this.connected) {
        hasNoEffectMessage = 'ignoring as the connection has been already established';
      } else if (this.isAttemptingToConnect) {
        hasNoEffectMessage = 'ignoring because already trying to reconnect';
      }

      Logger.logXMPPClient(__FILE__, __METHOD__, hasNoEffectMessage);
    }
  }

  disarmPingPongMonitoring = () => {
    this.pingIqId = undefined;
    this.missedPongsCount = 0;
    this.awaitPongResponseArmed = false;

    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = undefined;
    }
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = undefined;
    }
  }

  _finalizeDisconnection = () => {
    this.unacknowledgedStanzas = {};
    this.unacknowledgedStanzaRetryCounts = {};

    this.presenceConfirmationGranted = false;

    this._userAuth = '';
    this._curUserId = 0;
    this._curUserJID = '';
    this._calleeId = '';
    this._calleeJID = '';
    this.callInitiatorJID = null;

    this._crossTabEmitter && this._crossTabEmitter.removeAllListeners(CROSS_TAB_RINGING_EVENT);
    this._crossTabEmitter = undefined;

    this.connection.disconnect();
  }

  disconnect = () => {
    this.disarmPingPongMonitoring();

    this.retransmitUnacknowledgedStanzas()
      .then(this._finalizeDisconnection)
      .catch(this._finalizeDisconnection);
  };

  handleConnectionLoss = () => {
    this.props.changeConnectedState(false);
    this.connected = false;

    this.disarmPingPongMonitoring();
  }

  requestAcknowledgementForSentStanzas = (force = false) => {
    const __METHOD__ = 'Connection::requestAcknowledgementForSentStanzas';
    if (force || (Object.keys(this.unacknowledgedStanzas).length >= XMPP_MAX_UNACKNOWLEDGED_STANZAS)) {
      try {
        if (this.connection.connected) {
          this.connection.streamManagement.requestAcknowledgement();
        } else {
          Logger.logXMPPClient(__FILE__, __METHOD__, 'No connection available', LogLevel.Warning);
        }
      } catch (err) {
        Logger.logXMPPClient(
          __FILE__,
          __METHOD__,
          'Failed to send an acknowledgement request',
          LogLevel.Error,
          err,
        );
      }
    }
  }

  retransmitUnacknowledgedStanzas = () => new Promise((resolve, reject) => {
    const __METHOD__ = 'Connection::retransmitUnacknowledgedStanzas';

    const retransmissions = [];
    const me = this;

    const unacknowledgedStanzaIds = Object.keys(me.unacknowledgedStanzas);

    if (unacknowledgedStanzaIds.length === 0) {
      Logger.logXMPPClient(__FILE__, __METHOD__, 'no unacknowledged stanzas to retransmit');
      resolve();
      return;
    }

    try {
      unacknowledgedStanzaIds.forEach((stanzaId) => {
        const stanza = me.unacknowledgedStanzas[stanzaId];
        const retryCount = me.unacknowledgedStanzaRetryCounts[stanzaId] || 0;

        if (retryCount <= XMPP_MAX_RETRANSMIT_ATTEMPTS) {
          // We generally assume that the stored unacknowledged stanzas are
          // instances of Strophe.Builder, but still perform an additional check
          // just to be on the safe side.
          if (stanza && stanza.root && typeof stanza.root === 'function') {
            // If an outbound call is in progress and we do know that the call request message was
            // acknowledged - do not re-transmit any <request ...> stanzas to avoid unexpected
            // behavior on ejabberd side.
            if (me.callId && !me.amICallee && me.callRequestWasAcknowledged) {
              const [isCallRequest] = isCallRequestMsg(stanza);
              if (isCallRequest) {
                delete me.unacknowledgedStanzaRetryCounts[stanzaId];
              } else {
                retransmissions.push(stanza);
              }
            } else {
              retransmissions.push(stanza);
            }
          }
        } else {
          delete me.unacknowledgedStanzaRetryCounts[stanzaId];
        }
      });

      me.unacknowledgedStanzas = {};

      // Schedule a retransmission burst in the background.
      setTimeout(() => {
        try {
          Logger.logXMPPClient(
            __FILE__,
            __METHOD__,
            `Initiating a retransmission burst for ${retransmissions.length} stanzas`,
            LogLevel.Info,
            retransmissions.map((b) => redactChatMessageBody(b.tree())),
          );
          retransmissions.forEach((stanza) => {
            const stanzaId = stanza.tree().getAttribute('id');
            const retryCount = me.unacknowledgedStanzaRetryCounts[stanzaId] || 0;

            me.unacknowledgedStanzas[stanzaId] = stanza;
            me.unacknowledgedStanzaRetryCounts[stanzaId] = retryCount + 1;

            try {
              if (me.connection) {
                me.connection.send(stanza);
              } else {
                Logger.logXMPPClient(
                  __FILE__,
                  __METHOD__,
                  `unable to re-send stanza ${redactChatMessageBody(stanza.tree())}: no XMPP connection is available`,
                  LogLevel.Warning,
                );
              }
            } catch (sendErr) {
              Logger.logXMPPClient(
                __FILE__,
                __METHOD__,
                `unable to re-send stanza ${redactChatMessageBody(stanza.tree())}`,
                LogLevel.Warning,
                sendErr,
              );
            }
          });

          me.connection.streamManagement.requestAcknowledgement();

          Logger.logXMPPClient(__FILE__, __METHOD__, 'Retransmission burst complete');

          resolve();
        } catch (err) {
          Logger.logXMPPClient(__FILE__, __METHOD__, 'Retransmission burst failed', LogLevel.Error, err);

          reject(err);
        }
      }, 0);
    } catch (err) {
      Logger.logXMPPClient(__FILE__, __METHOD__, 'Preparing to retransmission failed', LogLevel.Error, err);

      reject(err);
    }
  });

  sendPing = () => {
    const __METHOD__ = 'Connection::sendPing';
    const __CALLBACK_METHOD_PONG__ = 'Connection::sendPing::pongTimer';
    const __CALLBACK_METHOD_SENDIQ__ = 'Connection::sendPing::sendIqCallback';

    if (this.connected && this._curUserJID) {
      if (this.unacknowledgedStanzas.length > 0) {
        // Request acknowledgement in lieu of a keep-alive message
        this.requestAcknowledgementForSentStanzas(true);
      } else {
        // No unacknowledged stanzas - send a regular ping IQ as a keep-alive message
        const id = this.connection.getUniqueId('ping');
        const iq = $iq({
          type: 'get',
          to: XMPP_DOMAIN,
          id,
        }).c('ping', { xmlns: Strophe.NS.PING });

        try {
          if (this.connection.connected) {
            this.connection.sendIQ(
              iq,
              () => {
                Logger.logWithTag(
                  __FILE__,
                  __CALLBACK_METHOD_SENDIQ__,
                  Logger.TAG_XMPP_CLIENT,
                  'Successfully sent a ping to the server',
                  LogLevel.Info,
                  undefined,
                  true,
                );
              },
              () => {
                Logger.logXMPPClient(__FILE__, __METHOD__, 'Sending a ping to a server failed', LogLevel.Warning);
              },
              RECONNECT_TIMEOUT_MS,
            );
          } else {
            Logger.logXMPPClient(__FILE__, __METHOD__, 'No connection available', LogLevel.Warning);
          }
        } catch (err) {
          Logger.logXMPPClient(__FILE__, __METHOD__, 'Failed to send a ping', LogLevel.Error, err);
        }

        if (this.pongTimer) {
          clearTimeout(this.pongTimer);
        }

        this.pingIqId = id;
        this.awaitPongResponseArmed = true;
        this.pongTimer = setTimeout(
          () => {
            if (this.awaitPongResponseArmed) {
              this.awaitPongResponseArmed = false;
              this.missedPongsCount += 1;
              if (this.missedPongsCount >= XMPP_MAX_MISSED_PONGS) {
                Logger.logXMPPClient(
                  __FILE__,
                  __CALLBACK_METHOD_PONG__,
                  'Exceeded the number of missed pong responses. Forcing a disconnect',
                  LogLevel.Warning,
                );
                setTimeout(() => { this.connection.disconnect(); }, 0);
              }
            }
          },
          XMPP_PONG_INTERVAL_MS,
        );
      }
    }
  }

  onConnect = (status, condition) => {
    const __METHOD__ = 'Connection::onConnect';

    if (status === Strophe.Status.CONNECTED) {
      Logger.logXMPPClient(__FILE__, __METHOD__, 'Strophe is connected');

      // Enable stream management
      this.connection.streamManagement.enable();
      Logger.logXMPPClient(__FILE__, __METHOD__, 'Stream management enabled');

      this.connection.addHandler(this.onMessage.bind(this), null, 'message', null, null, null);
      this.connection.addHandler(this.onIq.bind(this), null, 'iq', null, null, null);
      this.connection.addHandler(this.onPresenceSubscriptionRequest.bind(this), null, 'presence', 'subscribe', null, null, null);

      this.sendPresence();
      this.getServerTime();
      this.synchronize();

      if (this.initialised) {
        this.undoFinishCallOnXMPPConnectionLost();

        // We send the recovered stanza either for inbound calls (unconditionally),
        // or for outbound calls when we got an acknowledgement
        if (this.callId && (this.amICallee || this.callRequestWasAcknowledged)) {
          this.msgGenSend(
            this._curUserJID,
            this._calleeJID,
            'vcxep',
            'vcxep',
            {
              type: 'recovered',
              callid: this.callId,
            },
            undefined,
            true,
          );

          Logger.logXMPPClient(__FILE__, __METHOD__, `Sending a 'recovered' stanza for call ID: ${this.callId}`);
        }

        // Do not initiate retransmission immediately as there is a chance we might get more acknowledgements
        // from ejabberd after the connection has been established. Instead, request acknowledgement explicitly
        // and provide an one-time handler for the incoming acknowledgement stanza that will trigger the
        // retransmission.
        this.connection.streamManagement.onAcknowledgementComplete = () => {
          setTimeout(() => {
            this.retransmitUnacknowledgedStanzas().then(() => {
              this.events.emit(RECONNECTION_COMPLETE);
            });
          }, 0);

          return false;
        };
        this.connection.streamManagement.requestAcknowledgement();

        Logger.logXMPPClient(__FILE__, __METHOD__, 'RECONNECTED AGAIN');
      } else {
        Logger.logXMPPClient(__FILE__, __METHOD__, 'FIRST TIME CONNECTED');

        this.initialised = true;
      }

      this.props.changeConnectedState(true);
      this.connected = true;

      this.isAttemptingToConnect = false;
      clearTimeout(this.reconnectTimeout);

      this.pingTimer = setInterval(
        () => { this.sendPing(); },
        XMPP_PING_INTERVAL_MS,
      );
    } else if (status === Strophe.Status.CONNECTING) {
      Logger.logXMPPClient(__FILE__, __METHOD__, 'Strophe is connecting');

      // Prevent any concurrent reconnection attempts
      this.isAttemptingToConnect = true;
    } else if (status === Strophe.Status.CONNFAIL) {
      Logger.logXMPPClient(__FILE__, __METHOD__, `Strophe failed to connect: ${condition || 'no reason provided'}`, LogLevel.Warning);

      this.handleConnectionLoss();
      this.attemptToReconnect();
    } else if (status === Strophe.Status.DISCONNECTING) {
      Logger.logXMPPClient(__FILE__, __METHOD__, `Strophe is disconnecting: ${condition || 'no reason provided'}`, LogLevel.Warning);
      // Report the connection as unavailable ASAP to any consumers
      this.handleConnectionLoss();
    } else if (status === Strophe.Status.ERROR) {
      Logger.logXMPPClient(__FILE__, __METHOD__, `Strophe reported a connection error: ${condition || 'no details provided'}`, LogLevel.Warning);
      // Do nothing else as Strophe will force disconnection on its own
      // in case of a fatal error
    } else if (status === Strophe.Status.AUTHFAIL) {
      Logger.logXMPPClient(__FILE__, __METHOD__, `Strophe was unable to authenticate to ejabberd: ${condition || 'no details provided'}`, LogLevel.Error);
      // Assume the connection is unavailable if the client was unable
      // to authenticate to ejabberd
      this.handleConnectionLoss();
    } else if (status === Strophe.Status.DISCONNECTED) {
      Logger.logXMPPClient(__FILE__, __METHOD__, `Strophe is disconnected: ${condition || 'no reason provided'}`, LogLevel.Warning);

      if (this.callId && !this.isFinishCallOnXMPPConnectionLostTimerArmed()) {
        this.setFinishCallOnXMPPConnectionLost();
      }
      // Repeat reporting the no longer available connection just in case
      // there was no 'DISCONNECTING' notification from StropheJS
      this.handleConnectionLoss();
      this.attemptToReconnect();
    }
  };

  attemptToReconnect = () => {
    this.connection.reset();

    // Allow attempts to reconnect before setting the timer
    this.isAttemptingToConnect = false;

    if (this._curUserJID) {
      this.reconnectTimeout = setTimeout(() => {
        if (!this.isAttemptingToConnect) {
          this.props.reconnect(this.props.uuid);
        }
      }, RECONNECT_TIMEOUT_MS);
    }
  }

  onIq = (msg) => {
    const __METHOD__ = 'Connection::onIq';
    const __CALLBACK_METHOD_SENDIQ__ = 'Connection::onIq::sendIqCallback';

    if (!msg) return true;
    const type = msg.getAttribute('type');
    if (type === 'result') {
      const elems = msg.getElementsByTagName('history');
      if (elems.length > 0) {
        // Handle the message history response
        Logger.logChat(
          __FILE__,
          __METHOD__,
          `Got message history from the chat server; messages received: ${
            elems[0].childNodes ? elems[0].childNodes.length : 0
          }`,
        );

        const stanzaId = msg.getAttribute('id');

        setTimeout(() => {
          const historyRequestKey = Object.keys(this.messageHistoryIQIds).find((key) => this.messageHistoryIQIds[key] === stanzaId);
          if (historyRequestKey) {
            delete this.messageHistoryIQIds[historyRequestKey];
          }

          // If we got a reply to an <iq ... > request, the request
          // has been apparently received and handled by the server.
          delete this.unacknowledgedStanzas[stanzaId];
          delete this.unacknowledgedStanzaRetryCounts[stanzaId];
        }, 0);

        let isDisplayed = false;
        try {
          if (!elems[0].childNodes || elems[0].childNodes.length === 0) {
            return true;
          }

          const thread = Strophe.getText(elems[0].childNodes[0].getElementsByTagName('thread')[0]);

          const participants = thread ? thread.split('-') : [];

          const fromId = stanzaId.split(':history:')[1];

          const arr = [];

          elems[0].childNodes.forEach((child) => {
            let status = '';
            const timestamp = child.getAttribute('timestamp');
            const fromUserId = Strophe.getNodeFromJid(child.getAttribute('from'));
            const markedAsDisplayed = !!child.getElementsByTagName('displayed')[0];
            const iAmRecipient = Strophe.getNodeFromJid(child.getAttribute('to')) === this._curUserId.toString();

            if (markedAsDisplayed) {
              status = 'Read';
            }
            if (child.getElementsByTagName('sent')[0]) {
              status = 'Sent';
            }

            if (iAmRecipient && !markedAsDisplayed) {
              // Schedule sending the "displayed" chat status marker in the background.
              setTimeout(
                () => this.markChatMessage(this._curUserJID, generateJID(fromUserId, true), child.getAttribute('id'), thread, 'displayed'),
                0,
              );
              status = 'Read';
            }

            isDisplayed = isDisplayed || (
              // eslint-disable-next-line no-nested-ternary
              iAmRecipient ? markedAsDisplayed : true
            );

            const text = Strophe.getText(child.getElementsByTagName('body')[0]);
            const message = encodeXMPPmessage(text);
            message.status = status;
            message.timestamp = +timestamp;
            message.id = child.getAttribute('id');
            message.isMy = fromUserId && fromUserId === this._curUserId.toString();
            arr.push(message);
          });
          this.props.setMsgHistory(generateJID(fromId, false, this.props.uuid), arr.sort((a, b) => a.timestamp - b.timestamp), arr.length);
          if (isDisplayed) {
            this.offsetAutoGetHistory = 0;
          } else {
            this.offsetAutoGetHistory += consts.MES_HISTORY_SIZE;
            this.getChatMessages(...participants, this.offsetAutoGetHistory);
          }
        } catch (err) {
          Logger.logChat(
            __FILE__,
            __METHOD__,
            'Unexpected error while processing the received chat history',
            LogLevel.Error,
            err,
          );
        }
      } else if (msg.getAttribute('id').endsWith('time_1')) {
        // Handle the time offset response
        this.offset = getTime(new Date(msg.getElementsByTagName('utc')[0].innerHTML)) - getTime(new Date());
        this.props.setTimeOffset(this.offset);
      } else if (msg.getAttribute('id') === this.pingIqId) {
        // Handle the pong response
        this.awaitPongResponseArmed = false;
        clearTimeout(this.pongTimer);
      }
    } else if (type === 'get') {
      if (msg.firstElementChild
          && msg.firstElementChild.nodeName === 'ping'
          && msg.firstElementChild.namespaceURI === Strophe.NS.PING) {
        const pingFrom = msg.getAttribute('from');
        const pingId = msg.getAttribute('id');
        const pongIq = $iq({ type: 'result', to: pingFrom, id: pingId });
        try {
          if (this.connection.connected) {
            this.connection.sendIQ(pongIq);
            Logger.logWithTag(
              __FILE__,
              __CALLBACK_METHOD_SENDIQ__,
              Logger.TAG_XMPP_CLIENT,
              'Successfully sent a pong to the server',
              LogLevel.Info,
              undefined,
              true,
            );
          } else {
            Logger.logXMPPClient(
              __FILE__,
              __METHOD__,
              'No connection available to send a pong response',
              LogLevel.Warning,
            );
          }
        } catch (err) {
          Logger.logXMPPClient(
            __FILE__,
            __METHOD__,
            'No connection available to send a pong response',
            LogLevel.Error,
            err,
          );
        }
      }
    }
    // eslint-disable-next-line consistent-return
    return true;
  }

  /**
   * @param {Element} msg
   */
  onPresenceSubscriptionRequest = (msg) => {
    const __METHOD__ = 'Connection::onPresenceSubscriptionRequest';

    const requester = msg.getAttribute('from');
    if (!requester) {
      Logger.logXMPPClient(
        __FILE__,
        __METHOD__,
        'The sender of the incoming presence subscription request is unknown - no reply will be sent',
        LogLevel.Warning,
      );

      return;
    }

    const id = this.connection.getUniqueId('presence');
    const m = $pres({ to: requester, id, type: 'subscribed' });

    try {
      if (this.connection.connected) {
        this.connection.send(m);
      } else {
        Logger.logXMPPClient(
          __FILE__,
          __METHOD__,
          'No connection available to send presence subscription response',
          LogLevel.Warning,
        );
      }
    } catch (err) {
      Logger.logXMPPClient(
        __FILE__,
        __METHOD__,
        'Failed to send the presence subscription response',
        LogLevel.Error,
        err,
      );
    }
  }

  giveReasonForNotSendingAnSDPOffer = (signalingState) => {
    if (this.answering) {
      return 'because the callee role is not expected to generate offers';
    }
    if (signalingState === 'closed') {
      return 'because of an unsuitable signaling state';
    }

    return 'and the reason could not be determined';
  }

  onnegotiationneeded = (from) => {
    const __METHOD__ = 'Connection::onnegotiationneeded';

    if (this.webRtcPeers[from]) {
      const state = this.webRtcPeers[from].peerConnection.signalingState;
      Logger.logWebRTC(
        __FILE__,
        __METHOD__,
        `About to generate an SDP offer for a single peer: ${from}. The peer's current signaling state: ${state}. Is answering: ${!!this.answering}`,
      );
      const cInfo = this.props.getCInfo() || {};
      if (state !== 'closed' && !this.answering) {
        this.webRtcPeers[from].generateOffer((err, offerSdp) => {
          if (!err) {
            this.msgGenSend(this._curUserJID, from, 'vcxep', 'vcxep', { type: 'offerData', callid: cInfo.callId }, offerSdp, true);

            if (this.videoSender && this.videoSender.track) {
              this.videoSender.replaceTrack(this.localStream.getVideoTracks()[0]).then(() => {
                this.videoSender.track.enabled = true;
              }).catch((replaceTrackErr) => Logger.logWebRTC(
                __FILE__,
                __METHOD__,
                'Failed to replace an outgoing video track after sending an offer',
                LogLevel.Warning,
                replaceTrackErr,
              ));
            }
          } else {
            Logger.logWebRTC(
              __FILE__,
              __METHOD__,
              'Failed to generate an SDP offer',
              LogLevel.Error,
              err,
            );
            // & setCallState(NO_CALL);
          }
        });
      } else {
        Logger.logWebRTC(
          __FILE__,
          __METHOD__,
          `Did not generate an SDP offer for the peer: ${from} ${this.giveReasonForNotSendingAnSDPOffer(state)}`,
        );
      }
    } else {
      Logger.logWebRTC(
        __FILE__,
        __METHOD__,
        'Generating SDP offers for all known peers',
      );
      Object.keys(this.webRtcPeers).forEach((key) => {
        const state = this.webRtcPeers[key].peerConnection.signalingState;
        const cInfo = this.props.getCInfo() || {};

        Logger.logWebRTC(
          __FILE__,
          __METHOD__,
          `About to generate an SDP offer for a peer: ${key}. The peer's current signaling state: ${state}. Is answering: ${this.answering}`,
        );

        if (state !== 'closed' && !this.answering) {
          this.webRtcPeers[key].generateOffer((err, offerSdp) => {
            if (!err) {
              this.msgGenSend(this._curUserJID, key, 'vcxep', 'vcxep', { type: 'offerData', callid: cInfo.callId }, offerSdp, true);
            } else {
              Logger.logWebRTC(
                __FILE__,
                __METHOD__,
                'Failed to generate an SDP offer',
                LogLevel.Error,
                err,
              ); // & setCallState(NO_CALL);
            }
          });
        } else {
          Logger.logWebRTC(
            __FILE__,
            __METHOD__,
            `Did not generate an SDP offer for the peer: ${key} ${this.giveReasonForNotSendingAnSDPOffer(state)}`,
          );
        }
      });
    }
  }

  onRinging = (data) => {
    // React only on events emitted for current call.
    // React only on events emitted from other tabs (as each tab has its unique user JID).
    (this.callId && this.callId === data.callId)
    && (this._curUserJID && this._curUserJID !== data.currentUserJID)
    && this.props.changeCallRingtoneStatus(false);
  }

  onMessage = async (msg) => {
    const __METHOD__ = 'Connection::onMessage';
    const __CALLBACK_METHOD_PROCESS_ANSWER__ = 'Connection::onMessage::processAnswerCallback';

    const { to, from, type, id: messId, elems, thread, vcxepElems, timestamp, isMe } = processServerMsg(msg);
    const chatId = Strophe.getBareJidFromJid(from);
    if (type === 'chat' && elems.length > 0) {
      Logger.logXMPPReceive(
        __FILE__,
        __METHOD__,
        redactChatMessageBody(msg),
      );

      const _thread = Strophe.getText(thread[0]);
      const bodyText = Strophe.getText(elems[0]);
      const message = encodeXMPPmessage(bodyText);
      const userInfo = await processChatMsg(
        isMe ? to : from,
        this._curUserId,
        this._userAuth,
        this.props.changeUnreadNum,
        message,
        this.props.createNotification,
        (this.callState === CallState.INVITED || this.callState === CallState.ACCEPTED || this.callState === CallState.ACTIVE)
          ? undefined
          : this.props.messageAudio,
        this._calleeId,
        isMe,
      );
      message.timestamp = timestamp;
      if (isMe) {
        message.status = statuses.SENT;
      }
      this.props.setMsg(userInfo.id, userInfo.isUserPro, generateJID(userInfo.id, false, this.props.uuid), { ...message, id: messId, thread: _thread }, isMe);
      if (!isMe) {
        this.markChatMessage(this._curUserJID, generateJID(userInfo.id, true), messId, _thread, 'received');
      }
      if (userInfo) this.props.setUsr(userInfo);
    }

    if (type === 'chat_status') {
      Logger.logXMPPReceive(
        __FILE__,
        __METHOD__,
        msg.outerHTML,
      );

      let markedChatId = chatId;

      const displayedNode = msg.getElementsByTagName('displayed')[0];

      let receivedNode = msg.getElementsByTagName('received')[0];
      if (receivedNode && receivedNode.namespaceURI.startsWith('urn:xmpp:carbons:')) {
        // Unwrap the forwarded chat marker
        receivedNode = receivedNode.querySelector('forwarded message received');
        markedChatId = receivedNode ? Strophe.getBareJidFromJid(receivedNode.parentElement.getAttribute('from')) : markedChatId;
      }

      if (displayedNode) {
        const displayedMessageId = displayedNode.id;

        // We cannot simply use the chatId value here because in case of forwarded chat markers
        // the real sender's bare JID should be obtained from the forwarded message and not from
        // the outwards 'envelope' message.
        const parentTag = displayedNode.parentElement;
        markedChatId = (parentTag && parentTag.tagName === 'message')
          ? Strophe.getBareJidFromJid(parentTag.getAttribute('from'))
          : markedChatId;

        this.props.updateMsgStatus(markedChatId, displayedMessageId, statuses.READ);
      }

      if (receivedNode) {
        const receivedMessageId = receivedNode.id;
        clearTimeout(this[`failed-${receivedMessageId}-timeout`]);
        this.props.updateMsgStatus(markedChatId, receivedMessageId, statuses.DELIVERED);
      }
    }
    if ((type === 'error')) {
      Logger.logXMPPReceive(
        __FILE__,
        __METHOD__,
        redactChatMessageBody(msg),
      );

      if (this[`failed-${messId}-timeout`]) {
        this.props.updateMsgStatus(chatId, messId, statuses.SENT);
        clearTimeout(this[`failed-${messId}-timeout`]);
      }
    }
    if (type === 'badgeInfoUpdate') {
      const data = JSON.parse(elems[0].textContent);
      const toUser = await checkUserAndGet(data.personInfo.personID, this._userAuth);
      data.personInfo.toUser = toUser;
      this.props.changeUnreadNum(data);
    }

    if (type === 'vcxep' && vcxepElems.length > 0) {
      Logger.logXMPPReceive(
        __FILE__,
        __METHOD__,
        msg.outerHTML,
      );

      const body = vcxepElems[0];
      const { type: callType, callId, bodyInner } = processCallMsg(body);

      const cInfo = this.props.getCInfo() || {};
      const options = {
        localVideo: this.videoOutput,
        remoteVideo: this.videoInput,
        remoteAudio: this.audioInput,
        onicecandidate: onIceCandidate(this._curUserJID, this._calleeJID, cInfo.id || callId, this.msgGenSend),
        mediaConstraints: this.mediaConstraints,
        receiveConstraints: { video: true, audio: true },
        onnegotiationneeded: () => {
          if (!this.amICallee) {
            this.onnegotiationneeded(this._calleeJID);
          } else {
            this.offerRequest();
          }
        },
        configuration: {
          iceServers: [
            {
              urls: WEBRTC_STUN_PATH,
            },
            {
              urls: WEBRTC_TURN_PATH,
              username: TURN_USERNAME,
              credential: TURN_PASSWORD,
            },
          ],
        },
      };
      Logger.logSignaling(__FILE__, __METHOD__, `Incoming | ${callType}, for call ID: ${callId}`, body);
      switch (callType) {
        case 'request':
          if (cInfo.callId && cInfo.callId !== callId) {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              `Decision | rejecting the incoming call with busy status. Another call with cInfo.callId=${cInfo.callId} is in progress`,
            );
            this.callState = CallState.PRO_BUSY;
            this.msgGenSend(this._curUserJID, chatId, 'vcxep', 'vcxep', { type: 'proBusy', callid: callId }, undefined, true);

            break;
          } else if (this.callId && this.callId !== callId) {
            this.callState = CallState.PRO_BUSY;
            this.msgGenSend(this._curUserJID, chatId, 'vcxep', 'vcxep', { type: 'proBusy', callid: callId }, undefined, true);
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              `Decision | rejecting the incoming call with busy status. Another call with this.callId=${this.callId} is in progress`,
            );

            break;
          } else if (this.callId && this.callId === callId) {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              `Decision | ignoring the incoming call request with this.callId=${this.callId} as this call is already in progress`,
            );
            break;
          } else {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              `Decision | Initiating the call flow for call with this.callId=${this.callId}`,
            );
            this.callId = callId;
            this.callRequestWasAcknowledged = undefined;
            this.callWasRejected = false;
            this.callFailed = false;
            this.amICallee = true;
            this.waitData = true;
            this.wasAccepted = false;
            this.callProtocolVersion = this.getCallProtocolVersionFromCallRequest(body);
            this.incomingVideoEnabled = false;
            this.isOutgoingScreenSharingMuted = true;
            this.isOutgoingVideoMuted = true;
            this.isOutgoingAudioMuted = true;

            this.webRtcPeers = {};
            this.candidates = [];

            this.callInitiatorJID = chatId;

            this.callState = CallState.INVITED;

            // Arm a timer to automatically terminate the call if the call
            // did not connect within the allowed timeout.
            //
            // We pass an artificially constructed cInf instance to ensure
            // correct behavior of the rejectCall method at early stages
            // of the call flow before the call info was retrieved from the
            // back-end API and stored to the application-wide state.
            this.setProAutoDecline({
              callerId: Strophe.getNodeFromJid(chatId),
              // The person property here is not connected to featured calls, but is rather
              // a hack necessary to pass the caller's JID to rejectCall if the Pro auto-rejection
              // timer fires before a presenceConfirmationGranted signaling is received
              // (and hence before the communicateModal.callInfo state is populated and this._calleeJID is set).
              person: chatId,
            });
            this.events.emit(INCOMING_CALL_REQUEST_EVENT, callId);
          }

          const callerSupportsCallStatusRequest = this.isCallStatusRequestSupported(this.callProtocolVersion);
          Logger.logSignaling(
            __FILE__,
            __METHOD__,
            callerSupportsCallStatusRequest
              ? `Decision | The caller has indicated support for signaling protocol version ${this.callProtocolVersion}. Initiating the "callStatusRequest" handshake`
              : 'Decision | The caller has not indicated a supported signaling protocol version. Assuming the legacy "presenseConfirmationRequest" handshake',
          );

          const handshakeMessage = callerSupportsCallStatusRequest
            ? { type: 'callStatusRequest', callid: callId }
            : { type: 'presenseConfirmationRequest', callid: callId };
          this.msgGenSend(this._curUserJID, from, 'vcxep', 'vcxep', handshakeMessage, undefined, true);

          break;
        case 'dis_video':
          this.incomingVideoEnabled = false;
          this.props.changeVideoStreamStatus(false);
          break;
        case 'en_video':
          this.incomingVideoEnabled = true;
          this.playIncomingVideoStream(from);
          this.props.changeVideoStreamStatus(true);
          break;
        case 'receiverDeclined':
          if (this.callId && `${this.callId}` === `${callId}`) {
            if (this.callHasBeenAcceptedOnProsDevice()) {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | "receiverDeclined" signaling received '
                + ' but ignored as the call has been accepted on another Pro\'s device',
              );

              break;
            }

            this.props.caleeIsBusy();
            this.undoAutoReject();
            this.stopCommunication(false);
            this.callState = CallState.PRO_DECLINE;
            this.msgGenSend(this._curUserJID, Strophe.getBareJidFromJid(this._calleeJID), 'vcxep', 'vcxep', {
              type: 'declinedElsewhere',
              callid: cInfo.callId,
            });
          }
          break;
        case 'proBusy':
          if (this.callId && `${this.callId}` === `${callId}`) {
            if (this.callHasBeenAcceptedOnProsDevice()) {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | "proBusy" signaling received '
                + 'but ignored as the call has been already accepted on another Pro\'s device',
              );

              break;
            }
            this.props.caleeIsBusy();
            this.undoAutoReject();
            this.callState = CallState.PRO_BUSY;
            this.stopCommunication(false);
          }
          break;
        case 'callerDeclined':
          if (this.callId && `${this.callId}` === `${callId}`) {
            this.waitData = false;
            this.undoProAutoDecline();
            this.callState = CallState.CLIENT_DECLINE;
            this.stopCommunication();
          }

          if (!document.hasFocus()) {
            this.props.createNotification('Missed call', this.persons[from], 20000);
          }

          break;
        case 'noAnswer':
          if (!document.hasFocus()) {
            this.props.createNotification('Missed call', this.persons[from], 20000);
          }

          if (this.callId && `${this.callId}` === `${callId}`) {
            this.undoProAutoDecline();
            this.callState = CallState.NO_ANSWER;
            this.stopCommunication();
            if (this.waitVideoElemsInterval) clearTimeout(this.waitVideoElemsInterval);
          }

          break;
        case 'acceptedElsewhere':
          if (this.callId && `${this.callId}` === `${callId}`) {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              'Diagnostics | "acceptedElsewhere" received, current call info: '
              + `cInfo.callId=${cInfo.callId}, this.callId=${this.callId}`,
            );
            if (!this.wasAccepted) {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | "acceptedElsewhere" signaling received, but according to the app\'s internal state this device has not accepted the call. '
                + ' Stopping further communication.',
                LogLevel.Info,
                {
                  callWasAccepted: this.wasAccepted,
                  localStreamActive: this.localStream ? this.localStream.active : false,
                  localStreamID: this.localStream ? this.localStream.id : '',
                },
              );

              this.undoProAutoDecline();
              this.callState = CallState.IDLE;
              this.stopCommunication();
            } else {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | "acceptedElsewhere" signaling received, but we are the device'
                + ' that picked the call. Hence, ignoring the signaling.',
              );
            }
          } else {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              'Diagnostics | "acceptedElsewhere" received but ignored, as the call with id: '
              + `${callId} is not current`,
            );
          }
          break;
        case 'error':
          if (this.callId && `${this.callId}` === `${callId}`) {
            this.undoProAutoDecline();
            this.callState = CallState.FAILED;
            this.stopCommunication();
          }
          break;
        case 'declinedElsewhere':
          if (this.callId && `${this.callId}` === `${callId}`) {
            if (!this.callWasRejected) {
              this.undoProAutoDecline();
              this.callState = CallState.IDLE;
              this.stopCommunication();
            } else {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | ignoring the "declinedElsewhere" message as we are the device that sent it',
              );
            }
          }
          break;
        case 'forbidden':
          if (this.callId && `${this.callId}` === `${callId}`) {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              `Decision | the call with call ID ${callId} was forbidden, stopping communication and disarming the timers`,
            );
            this.stopWaitingVideoElements();
            this.undoAutoReject();
            this.undoProAutoDecline();
            this.props.caleeIsBusy();
            this.candidates = [];
            // this.stopCommunication();
            this.callState = CallState.FAILED;
            this.events.emit(CALL_FAILED_EVENT, this.amICallee, 'The call has forbidden');
          }
          break;
        case 'failed':
          if (this.callId && `${this.callId}` === `${callId}`) {
            if (!this.amICallee) {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | "failed" signaling received but ignored in the caller (Client) role',
              );

              break;
            }

            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              `Diagnostics | the call with call ID ${callId} failed on the other side`,
              LogLevel.Warning,
              bodyInner,
            );

            const { amICallee } = this;

            this.callState = CallState.FAILED;
            this.events.emit(CALL_FAILED_EVENT, amICallee, `${
              amICallee
                ? 'The call has finished unexpectedly on the Client side'
                : 'The Pro was unable to accept your call'
            }`);
          }
          break;
        case 'granted':
          if (this.callId && `${this.callId}` === `${callId}`) {
            if (this.callHasBeenAcceptedOnProsDevice()) {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | ignoring the "granted" message as the call has been already accepted on another Pro\'s device',
              );
              return;
            }

            this.amICallee = false;

            if (!this.webRtcPeers[from]) {
              this.props.processCall();

              // timer ???
              this.rescanMediaDevices(this.shouldForceAudioPermissionCheck()).then(() => {
                // Need to capture "from" in a closure so that we can use the captured opponent's JID
                // in the error handler.
                const peerConnectionCreationFailedHandler = this.makePeerConnectionCreationFailedHandler(from);

                navigator.mediaDevices.getUserMedia(this.mediaConstraints).then((capturedStream) => {
                  if ((this.callId && `${this.callId}` === `${callId}`) && (!this.callWasRejected && !this.callFailed)) {
                    this.webRtcPeers[from] = createPeerConnection(
                      this._curUserJID,
                      from,
                      this.msgGenSend,
                      {
                        ...options,
                        onicecandidate: onIceCandidate(this._curUserJID, from, cInfo.id || callId, this.msgGenSend),
                        onnegotiationneeded: () => this.onnegotiationneeded(from),
                        mediaConstraints: { ...this.mediaConstraints },
                        videoStream: capturedStream,
                      },
                      cInfo,
                      peerConnectionCreationFailedHandler,
                      this.canPlayCalleesAudioTrack,
                      this.shallMuteOutgoingAudio,
                      this.canPlayIncomingVideo,
                    );

                    this.videoSender = null;

                    this.localStream = capturedStream;

                    this.connectionQualityMonitor.resetStats();

                    this.setPeerStateControl(this.webRtcPeers[from].peerConnection, from);
                  }
                }).catch((err) => this.failCall(this.tryGetMediaCaptureErrorReason(err), false));
              }).catch((err) => this.failCall(this.tryGetMediaCaptureErrorReason(err), false));
            } else {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | duplicated "granted" message has been ignored, '
                + `as an RTCPeerConnection for callee's device '${from}' has been already created`,
              );
            }
          }
          break;
        case 'offerData':
          if (this.callId && `${this.callId}` === `${callId}`) {
            if (this.callWasRejected || this.callFailed) {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                `Decision | offer data received but ignored as the call ${this.callFailed ? 'failed' : 'was already rejected'}`,
              );
              return;
            }

            if (this.webRtcPeers[from] && this.webRtcPeers[from].peerConnection.signalingState !== 'stable') {
              Logger.logWebRTC(
                __FILE__,
                __METHOD__,
                'Offer data received but ignored due to an inappropriate '
                + `RTCPeerConnection signaling state: "${this.webRtcPeers[from].peerConnection.signalingState}"`,
              );
              return;
            }

            Logger.logWebRTC(
              __FILE__,
              __METHOD__,
              'Received SDP offer data from the remote peer',
              LogLevel.Info,
              bodyInner,
            );

            this.answering = !this.webRtcPeers[from];

            if (this.waitVideoElemsInterval) clearTimeout(this.waitVideoElemsInterval);
            this.waitVideoElemsInterval = setInterval(() => {
              // eslint-disable-next-line no-unused-expressions
              (this.videoInput && this.videoOutput)
              && this.audioInput
              && (this.callId && `${this.callId}` === `${callId}`)
              && (!this.callWasRejected && !this.callFailed)
              && (
                this.props.processCall(),
                (() => {
                  options.localVideo = this.videoOutput;
                  options.remoteVideo = this.videoInput;
                  options.remoteAudio = this.audioInput;
                })(),
                clearInterval(this.waitVideoElemsInterval),
                (() => {
                  if (!this.webRtcPeers[from]) this.videoSender = null;
                })(),
                // eslint-disable-next-line no-return-assign
                (() => {
                  if (this.webRtcPeers[from]) {
                    // A WebRTC connection already exists; just reply with an SDP answer.
                    sendInCallAnswerData(this._curUserJID, this._calleeJID, this.webRtcPeers[from], bodyInner, this.msgGenSend, cInfo, () => {
                      if (this.videoSender) {
                        this.videoSender.replaceTrack(this.localStream.getVideoTracks()[0]).then(() => {
                          this.videoSender.track.enabled = true;
                        }).catch((replaceTrackErr) => Logger.logWebRTC(
                          __FILE__,
                          __METHOD__,
                          'Failed to replace an outgoing video track after sending an offer',
                          LogLevel.Warning,
                          replaceTrackErr,
                        ));
                      }
                    });
                  } else {
                    // A WebRTC connection has not been created yet; force peer connection creation before
                    // replying with an SDP answer.
                    this.rescanMediaDevices(this.shouldForceAudioPermissionCheck()).then(() => {
                      navigator.mediaDevices.getUserMedia(this.mediaConstraints).then((capturedStream) => {
                        if ((this.callId && `${this.callId}` === `${callId}`) && (!this.callWasRejected && !this.callFailed)) {
                          this.webRtcPeers[from] = sendAnswerData(
                            this._curUserJID,
                            this._calleeJID,
                            bodyInner,
                            this.msgGenSend,
                            {
                              ...options,
                              mediaConstraints: { ...this.mediaConstraints },
                              videoStream: capturedStream,
                            },
                            cInfo,
                            (err) => {
                              this.answering = false;
                              if (err) {
                                this.failCall(err, true);
                              }
                            },
                            this.canPlayCallerAudioTrack,
                            this.shallMuteOutgoingAudio,
                            this.canPlayIncomingVideo,
                          );

                          this.localStream = capturedStream;

                          this.setPeerStateControl(this.webRtcPeers[from].peerConnection, from);

                          Logger.logWebRTC(
                            __FILE__,
                            __METHOD__,
                            `Diagnostics | Local HTML elements for the audio and video streams became available. WebRTC peer connection to ${from} created.`,
                          );

                          this.addAccumulatedRemoteCandidatesToPeerConnection(this.webRtcPeers[from], from);
                        }
                      }).catch((err) => this.failCall(this.tryGetMediaCaptureErrorReason(err), true));
                    }).catch((err) => this.failCall(this.tryGetMediaCaptureErrorReason(err), true));
                  }
                })(),
                this.waitVideoElemsInterval = null
              );
            }, 100);
          } else {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              `Decision | Ignoring the received offer data for call ID ${callId} which is not current`,
            );
          }
          break;
        case 'answerData':
          if (this.callId && `${this.callId}` === `${callId}`) {
            if (this.callHasBeenAcceptedOnProsDevice() && (from !== this._calleeJID)) {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | ignoring the "answerData" message from any other Pro\'s devices as the call has been already accepted on another Pro\'s device',
              );
              return;
            }

            if (this.webRtcPeers[from]) {
              if (this.webRtcPeers[from].peerConnection.signalingState === 'stable'
                || this.webRtcPeers[from].peerConnection.signalingState === 'have-remote-pranswer') {
                Logger.logWebRTC(
                  __FILE__,
                  __METHOD__,
                  'Answer data received but ignored due to an inappropriate '
                  + `RTCPeerConnection signaling state: '${this.webRtcPeers[from].peerConnection.signalingState}'`,
                );

                return;
              }

              Logger.logWebRTC(
                __FILE__,
                __METHOD__,
                'Processing the received SDP answer',
                LogLevel.Info,
                bodyInner,
              );

              this.webRtcPeers[from].processAnswer(bodyInner, (err) => {
                if (!err) {
                  Logger.logWebRTC(
                    __FILE__,
                    __CALLBACK_METHOD_PROCESS_ANSWER__,
                    'The received SDP answer has been processed',
                  );
                } else {
                  Logger.logWebRTC(
                    __FILE__,
                    __CALLBACK_METHOD_PROCESS_ANSWER__,
                    'Failed to process the received SDP answer',
                    LogLevel.Error,
                    err,
                  );
                }
              });
            }
          } else {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              `Decision | Ignoring the received answer data for call ID ${callId} which is not current`,
            );
          }
          break;
        case 'accept':
          if (this.callId && `${this.callId}` === `${callId}`) {
            if (this.callHasBeenAcceptedOnProsDevice()) {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | Ignoring the received "accepted" message as the call has been accepted on another Pro\'s device',
              );
              break;
            }

            this.undoAutoReject();
            this.undoProAutoDecline();
            this._calleeJID = from;
            this.callState = CallState.ACCEPTED;
            this.msgGenSend(this._curUserJID, this._calleeJID, 'vcxep', 'vcxep', {
              type: 'speaking',
              callid: cInfo.callId,
            });
            this.msgGenSend(this._curUserJID, Strophe.getBareJidFromJid(this._calleeJID), 'vcxep', 'vcxep', {
              type: 'acceptedElsewhere',
              callid: cInfo.callId,
            });
            this.requestAcknowledgementForSentStanzas(true);
            Object.keys(this.webRtcPeers).forEach((key) => {
              if (key !== from) {
                if (this.webRtcPeers[key]) {
                  try {
                    this.webRtcPeers[key].dispose();
                  // eslint-disable-next-line no-empty
                  } catch (_disposeErr) { }
                  delete this.webRtcPeers[key];
                }
              }
            });
          }
          break;
        case 'speaking':
          if (this.callId && `${this.callId}` === `${callId}`) {
            if (this.callHasBeenAcceptedOnProsDevice() && (from !== this._calleeJID)) {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | Ignoring the received "speaking" message as the call has been accepted on another Pro\'s device',
              );
              break;
            }
            this.callState = CallState.ACTIVE;
            this.undoProAutoDecline();
            this.muteAudio(false);
            this.muteOpponentAudio(false);
            this.props.speaking();
            this.events.emit(CALL_CONNECTED_EVENT, this.callId);

            // Force audio stream from the device that accepted the call.
            if (this.webRtcPeers[from] && this.audioInput) {
              const thePeerConnection = this.webRtcPeers[from].peerConnection;

              const remoteStreams = thePeerConnection.getRemoteStreams();
              let audioStream;

              for (let i = 0; i < remoteStreams.length; i += 1) {
                const tracks = remoteStreams[i].getVideoTracks();
                if (remoteStreams[i].active) {
                  if (!tracks.length) {
                    audioStream = remoteStreams[i];
                  }
                }
              }

              if (audioStream) {
                if (this.audioInput.srcObject && this.audioInput.srcObject === audioStream) {
                  Logger.logWebRTC(__FILE__, __METHOD__, 'Incoming audio stream has been already connected to audio playback - nothing to do');
                } else {
                  this.audioInput.srcObject = audioStream;
                  this.audioInput.load();
                  this.audioInput.play().catch((e) => {
                    safeSetMediaPlaybackState('none');

                    Logger.logWebRTC(
                      __FILE__,
                      __METHOD__,
                      `Failed to play the incoming audio stream. An error occurred: ${e.name || 'unknown error'} with message '${e.message}'`,
                      LogLevel.Warning,
                    );
                  });
                  safeSetMediaPlaybackState('none');
                  Logger.logWebRTC(__FILE__, __METHOD__, 'Incoming audio stream already present - connecting to audio playback');
                }
              } else {
                Logger.logWebRTC(__FILE__, __METHOD__, 'Incoming audio stream not yet present - waiting for an incoming audio track');
              }
            }
          }
          break;
        case 'avtimeEnded':
        case 'moneyEnded':
          if (this.callId && `${this.callId}` === `${callId}`) {
            this.props.setAutoFinishNotif();
            this.setAutoFinish();
          }
          break;
        case 'preBooking':
          if (this.callId && `${this.callId}` === `${callId}`) {
            this.props.setAutoFinishPreBookingNotif();
            this.setAutoFinish();
          }
          break;
        case 'bookingEnded':
          if (this.callId && `${this.callId}` === `${callId}`) {
            this.props.setAutoFinishBookingEndedNotif();
            this.setAutoFinish();
          }
          break;
        case 'callDisconnected':
          const currentCall = `${this.callId}` === `${callId}`;

          Logger.logSignaling(
            __FILE__,
            __METHOD__,
            `Diagnostics | Received "callDisconnected"; call is ${currentCall ? '' : 'not'} current`,
          );

          if (this.callId && `${this.callId}` === `${callId}`) {
            this.undoProAutoDecline();
            this.stopCommunication();
          }
          break;
        case 'finished':
          if (this.callId && `${this.callId}` === `${callId}`) {
            this.undoProAutoDecline();
            this.stopCommunication();
          }
          break;
        case 'offerRequest':
          this.onnegotiationneeded(this._calleeJID);
          break;
        case 'presenseConfirmationRequest':
          this.msgGenSend(this._curUserJID, from, 'vcxep', 'vcxep', { type: 'presenseConfirmationGranted', callid: cInfo.callId }, undefined, true);
          break;
        case 'callStatusRequest':
          const statusResponseElementOptions = {
            type: 'callStatusResponse',
            callid: callId,
          };
          let statusResponseBody;

          this.callProtocolVersion = MIN_SIGNALING_PROTOCOL_SUPPORTING_CALLSATUS_REQUEST;
          Logger.logSignaling(
            __FILE__,
            __METHOD__,
            `Decision | Received a "callStatusRequest" message from the callee. Assuming support for signaling protocol version ${MIN_SIGNALING_PROTOCOL_SUPPORTING_CALLSATUS_REQUEST} or higher`,
          );

          if (!this.callId || (this.callId && `${this.callId}` !== `${callId}`)) {
            // No active call, or another call in progress
            statusResponseBody = 'outdated';
          } else if (this.callId && `${this.callId}` === `${callId}`) {
            // The request is for current call - checking call status
            if (this.callState === CallState.CLIENT_DECLINE
              || this.callState === CallState.NO_ANSWER
              || this.callState === CallState.PRO_BUSY
              || this.callState === CallState.PRO_DECLINE
              || this.callStete === CallState.FAILED
              || this.callState === CallState.DISCONNECTED
              || this.callState === CallState.BREAK
              || this.callState === CallState.IDLE
              || this.callState === CallState.SUCCEEDED
            ) {
              // Current call is no longer active
              statusResponseBody = 'outdated';
            } else if (this.callHasBeenAcceptedOnProsDevice()) {
              statusResponseBody = 'answeredElsewhere';
            } else {
              statusResponseBody = 'active';
            }
          } else {
            statusResponseBody = 'outdated';
          }

          this.msgGenSend(this._curUserJID, from, 'vcxep', 'vcxep', statusResponseElementOptions, statusResponseBody, true);

          break;
        case 'presenseConfirmationGranted':
          if (this.presenceConfirmationGranted) {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              'Decision | ignoring the received \'presenseConfirmationGranted\' message'
              + ` for call ID ${callId} as presence has been already confirmed.`
              + ` current call info: cInfo.callId=${cInfo.callId}, this.callId=${this.callId}`,
            );
            return;
          }

          this.presenceConfirmationGranted = true;

          this.prepareForIncomingCall(callId, from, options, cInfo);

          break;
        case 'callStatusResponse':
          if (this.presenceConfirmationGranted) {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              'Decision | ignoring the received \'callStatusResponse\' message'
              + ` for call ID ${callId} as call status has been already confirmed.`
              + ` current call info: cInfo.callId=${cInfo.callId}, this.callId=${this.callId}`,
            );
            return;
          }

          if (this.callState === CallState.PRO_TIMEOUT) {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              'Decision | ignoring the received \'callStatusResponse\' message'
              + ` for call ID ${callId} as the call has timed out on the Pro's side.`
              + ` current call info: cInfo.callId=${cInfo.callId}, this.callId=${this.callId}`,
            );
            return;
          }

          if (this.callId && `${this.callId}` === `${callId}`) {
            switch (bodyInner) {
              case 'active':
                this.presenceConfirmationGranted = true;
                this.prepareForIncomingCall(callId, from, options, cInfo);

                Logger.logSignaling(
                  __FILE__,
                  __METHOD__,
                  'Decision | allowing the incoming call to go through'
                  + ' as the call status was reported as still active',
                );

                break;
              case 'answeredElsewhere':
              case 'outdated':
                this.undoProAutoDecline();
                this.callState = CallState.IDLE;
                this.stopCommunication();

                Logger.logSignaling(
                  __FILE__,
                  __METHOD__,
                  'Decision | aborting the incoming call'
                  + ` with call ID ${callId} as the call status reported by the caller was "${bodyInner}"`,
                );

                break;
              default: break;
            }
          } else {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              'Decision | ignoring the incoming "callStatusResponse" message'
              + ` for call ID ${callId} as the call is not current`,
            );
          }

          break;
        case 'candidateData':
          if (this.callId && `${this.callId}` === `${callId}`) {
            if (this.callHasBeenAcceptedOnProsDevice() && (from !== this._calleeJID)) {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | ignoring the "candidateData" message from any other Pro\'s devices as the call has been already accepted on another Pro\'s device',
              );
              return;
            }

            if (this.callWasRejected || this.callFailed) {
              Logger.logSignaling(
                __FILE__,
                __METHOD__,
                'Decision | candidate data received but discarded, '
                + `because the current call has ${this.callFailed ? 'failed' : 'been rejected'}`,
              );
              return;
            }

            const obj = JSON.parse(bodyInner);
            // check for the Object instance
            obj.forEach((c) => {
              const candidate = {
                candidate: c.sdp,
                sdpMid: c.sdpMid,
                sdpMLineIndex: c.index,
                receivedFrom: from,
              };
              if (this.webRtcPeers[from]) {
                this.addICECandidateToPeerConnection(this.webRtcPeers[from], candidate);
              } else {
                Logger.logWebRTC(
                  __FILE__,
                  __METHOD__,
                  'No peer connection has been created yet, '
                  + 'remembering a received candidate',
                  LogLevel.Info,
                  candidate.candidate,
                );
                this.candidates.push(candidate);
              }
            });
          } else {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              'Decision | ignoring the received candidate data'
              + ` for call ID ${callId} as the call is not current`,
            );
          }
          break;
        case 'rtcConnectionLost':
          Logger.logSignaling(
            __FILE__,
            __METHOD__,
            `Diagnostics | "rtcConnectionLost" received for call id ${callId}, current call info: `
            + `cInfo.callId=${cInfo.callId}, this.callId=${this.callId}`,
          );

          this.props.undoAutoFinishNotif();
          this.undoAutoFinish();
          if (this.callId && `${this.callId}` === `${callId}`) {
            this.undoProAutoDecline();
            this.callState = CallState.DISCONNECTED;
            this.stopCommunication();
          }
          break;
        default:
          break;
      }
    }

    // eslint-disable-next-line consistent-return
    return true;
  }

  onAcknowledgedStanza = (id) => {
    const __METHOD__ = 'Connection::onAcknowledgedStanza';

    if (Object.prototype.hasOwnProperty.call(this.unacknowledgedStanzas, id)) {
      Logger.logXMPPClient(
        __FILE__,
        __METHOD__,
        `acknowledged message ID: ${id}`,
      );

      if (this.callId && !this.amICallee) {
        const [isRequestMsg, callId] = isCallRequestMsg(this.unacknowledgedStanzas[id]);
        if (isRequestMsg && (`${this.callId}` === `${callId}`)) {
          this.callRequestWasAcknowledged = true;
        }
      }

      delete this.unacknowledgedStanzas[id];
      delete this.unacknowledgedStanzaRetryCounts[id];
    }
  }

  setPeerStateControl = (pc, remoteParty) => {
    const __METHOD__ = 'Connection::setPeerStateControl';

    // eslint-disable-next-line no-param-reassign
    pc.oniceconnectionstatechange = () => this.connsectionIceStateCallback(pc);
    // eslint-disable-next-line no-param-reassign
    pc.onicecandidateerror = (errEvent) => {
      Logger.logWebRTC(
        __FILE__,
        __METHOD__,
        `Error ${errEvent.errorCode}`
        + ` while reaching the STUN/TURN server at ${errEvent.url}: ${errEvent.errorText}`,
        LogLevel.Warning,
        errEvent,
      );
    };
    // eslint-disable-next-line no-param-reassign
    pc.onconnectionstatechange = () => {
      switch (pc.connectionState) {
        case 'connected':
          this.props.changeReconnectionStatus(false);
          break;
        case 'disconnected':
        case 'failed':
          this.props.changeReconnectionStatus(true);
          break;
        default:
          break;
      }
    };
    // eslint-disable-next-line no-param-reassign
    pc.onsignalingstatechange = () => {
      Logger.logWithTag(__FILE__, __METHOD__, Logger.TAG_WEBRTC, `Signaling state for the remote participant ${remoteParty} changed to: ${pc.signalingState}`, LogLevel.Debug, undefined, false);
    };
  }

  addICECandidateToPeerConnection = (peerConnection, candidate) => {
    const __METHOD__ = 'Connection:addICECandidateToPeerConnection';

    peerConnection.addIceCandidate(
      candidate,
      (err) => {
        if (err) {
          Logger.logWebRTC(
            __FILE__,
            __METHOD__,
            `Failed to add an ICE candidate: ${err.name}`,
            LogLevel.Warning,
            { err, candidate },
          );
        } else {
          Logger.logWebRTC(
            __FILE__,
            __METHOD__,
            `Successfully added an ICE candidate: ${candidate.candidate}`,
          );
        }
      },
    );
  }

  initiateICERestart = (peerConnection) => {
    const __METHOD__ = 'Connection::initiateICERestart';

    if (peerConnection.iceConnectionState === 'disconnected' || peerConnection.iceConnectionState === 'failed') {
      if (peerConnection.signalingState !== 'have-local-offer' && peerConnection.signalingState !== 'have-remote-pranswer') {
        Logger.logWebRTC(
          __FILE__,
          __METHOD__,
          'Attempting to restore the ICE connection from the caller side',
        );
        peerConnection
          .createOffer({ iceRestart: true })
          .then((offer) => this.successRestart(offer))
          .catch((reason) => this.failRestart(reason));
      } else {
        Logger.logWebRTC(
          __FILE__,
          __METHOD__,
          'ICE restore timer has fired, but the peer connection is'
          + ` already in progress of a negotiation. Current peer connection signaling state: ${peerConnection.signalingState}`,
          LogLevel.Warning,
        );
      }
    } else {
      Logger.logWebRTC(
        __FILE__,
        __METHOD__,
        'ICE restore timer has fired, but the peer connection is'
        + ` not in a failed / disconnected state. Current peer connection state: ${peerConnection.signalingState}`,
        LogLevel.Info,
      );
    }
  }

  requestConnectionStats = async (peerConnection) => {
    const stats = await peerConnection.getStats();
    this.connectionQualityMonitor.processStatsReport(stats);
  }

  connsectionIceStateCallback = (pc) => {
    const __METHOD__ = 'Connection::connsectionIceStateCallback';

    if (pc) {
      if (pc.iceConnectionState === 'connected') {
        this.connectionStatsTimer = setInterval(() => this.requestConnectionStats(pc), 1000);

        if (this.connectionLostTimeout) {
          Logger.logWebRTC(
            __FILE__,
            __METHOD__,
            'The ICE connection has been re-established',
          );

          clearTimeout(this.connectionLostTimeout);
          this.connectionLostTimeout = null;

          if (this.restoreIceInterval) {
            clearInterval(this.restoreIceInterval);
            this.restoreIceInterval = undefined;
          }

          this.props.changeReconnectionStatus(false);
        }
      }
      if ((pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') && this.callId) {
        Logger.logWebRTC(
          __FILE__,
          __METHOD__,
          'the ICE connection has '
          + `${pc.iceConnectionState === 'failed' ? 'failed' : 'become disconnected'}`,
          LogLevel.Warning,
        );

        this.props.changeReconnectionStatus(true);

        // Cancel connection stats gathering
        if (this.connectionStatsTimer) {
          clearInterval(this.connectionStatsTimer);
          this.connectionStatsTimer = undefined;
        }

        // Cancel (if needed) and restart the connection lost timer
        clearTimeout(this.connectionLostTimeout);
        this.connectionLostTimeout = null;
        this.rtcConnectionLost();

        if (!this.amICallee) {
          // If ICE is in the failed state, it makes sense to initiate an ICE restart immediately.
          // Otherwise, it makes sense to wait several seconds before making the first attempt,
          // as the 'disconnected' state is intermittent and could heal itself rather quickly
          // without our intervention.
          //
          // But even in the 'failed' state, we still set up the restore ICE timer so that
          // we make subsequent attempts to re-establish the ICE connection.

          if (pc.iceConnectionState === 'failed') {
            this.initiateICERestart(pc);
          }

          clearInterval(this.restoreIceInterval);
          this.restoreIceInterval = setInterval(() => {
            this.initiateICERestart(pc);
          }, WEBRTC_ICE_RESTART_DELAY_AFTER_DISCONNECTED);
        }
      }
    }
  }

  sendNewOfferToRestartee = () => {
    const __METHOD__ = 'Connection::sendNewOfferToRestartee';

    const cInfo = this.props.getCInfo() || {};
    this.msgGenSend(
      this._curUserJID,
      this._calleeJID,
      'vcxep',
      'vcxep',
      { type: 'offerData', callid: cInfo.callId },
      this.webRtcPeers[this._calleeJID].peerConnection.localDescription.sdp,
      true,
    );
    Logger.logWebRTC(
      __FILE__,
      __METHOD__,
      'The new offer for the ICE restart has been sent',
    );
  }

  failRestart = (reason) => {
    const __METHOD__ = 'Connection::failRestart';
    Logger.logWebRTC(
      __FILE__,
      __METHOD__,
      'ICE restart failed with reason',
      LogLevel.Error,
      reason,
    );
  }

  successRestart = (offer) => {
    const __METHOD__ = 'Connection::successRestart';

    if (this._calleeJID && this.webRtcPeers[this._calleeJID]) {
      this.webRtcPeers[this._calleeJID].peerConnection.setLocalDescription(offer)
        .then(() => {
          Logger.logWebRTC(
            __FILE__,
            __METHOD__,
            'The new offer for the ICE restart has been applied locally',
          );
          this.sendNewOfferToRestartee();
        })
        .catch((err) => {
          Logger.logWebRTC(
            __FILE__,
            __METHOD__,
            'Failed to apply the local description during an ICE restart',
            LogLevel.Error,
            err,
          );
        });
    } else {
      Logger.logSignaling(
        __FILE__,
        __METHOD__,
        `Diagnostics | no callee JID set, or a peer connection does not exist for the callee ${this._calleeJID}`,
        LogLevel.Warning,
      );
    }
  }

  // -- Timeouts start ---
  setAutoReject = () => {
    const __METHOD__ = 'Connection::setAutoReject::timerCallback';
    this.autoRejectTimeout = setTimeout(() => {
      Logger.logSignaling(
        __FILE__,
        __METHOD__,
        'Decision | The Client\'s call auto-rejection timer has fired - rejecting the outgoing call',
        LogLevel.Warning,
      );

      const cInfo = this.props.getCInfo() || {};
      this.msgGenSend(this._curUserJID, this._calleeJID, 'vcxep', 'vcxep', { type: 'noAnswer', callid: cInfo.callId }, undefined, true);
      this.callState = CallState.NO_ANSWER;
      this.stopCommunication();
    }, this.callTimeBeep);
  }

  undoAutoReject = () => {
    clearTimeout(this.autoRejectTimeout);
    this.autoRejectTimeout = null;
  }

  setAutoFinish = () => {
    this.autoFinishTimeout = setTimeout(() => this.finishCall(true), timeouts.autoFinishCallTimeout);
  }

  undoAutoFinish = () => {
    clearTimeout(this.autoFinishTimeout);
    this.autoFinishTimeout = null;
  }

  setProAutoDecline = (cInf = {}) => {
    const __METHOD__ = 'Connection::setProAutoDecline::timerCallback';
    this.proAutoDeclineTimeout = setTimeout(async () => {
      Logger.logSignaling(
        __FILE__,
        __METHOD__,
        'Decision | The Pro auto-decline timer has fired - rejecting the incoming call',
        LogLevel.Warning,
      );

      this.callState = CallState.PRO_TIMEOUT;
      // TODO: Ensure that the call has not been already answered

      this.rejectCall(false, cInf);
    }, this.callTimeBeep);
  }

  undoProAutoDecline = () => {
    if (this.proAutoDeclineTimeout) {
      clearTimeout(this.proAutoDeclineTimeout);
    }

    this.proAutoDeclineTimeout = undefined;
  }

  setFinishCallOnXMPPConnectionLost = () => {
    const __METHOD__ = 'Connection::setFinishCallOnXMPPConnectionLost';

    this.finishCallOnXMPPConnectionLostTimeout = setTimeout(() => {
      Logger.logSignaling(
        __FILE__,
        __METHOD__,
        'Timer fired, terminating the call',
        LogLevel.Warning,
      );

      if (this.callId && this._calleeJID) {
        // Sending rtcConnectionLost (even though this message does not accurately describe the situation) is unfortunately
        // our best bet as there's no other signaling message that would terminate the call with the DISCONECTED status.
        this.msgGenSend(this._curUserJID, this._calleeJID, 'vcxep', 'vcxep', { type: 'rtcConnectionLost', callid: this.callId }, undefined, true);
      }

      this.undoAutoReject();
      this.undoAutoFinish();
      this.undoProAutoDecline();

      this.props.undoAutoFinishNotif();

      this.callState = CallState.DISCONNECTED;

      this.stopCommunication();
    }, timeouts.finishOnXMPPConnectionLostTimeout);
  }

  undoFinishCallOnXMPPConnectionLost = () => {
    if (this.finishCallOnXMPPConnectionLostTimeout) {
      clearTimeout(this.finishCallOnXMPPConnectionLostTimeout);
      this.finishCallOnXMPPConnectionLostTimeout = undefined;
    }
  }

  isFinishCallOnXMPPConnectionLostTimerArmed = () => this.finishCallOnXMPPConnectionLostTimeout !== undefined;
  // -- Timeouts end ---

  prepareForIncomingCall = async (callId, from, _rtcOptions, _cInfo) => {
    // eslint-disable-next-line no-unused-vars
    const __METHOD__ = 'Connection::prepareForIncomingCall';

    this.presenceConfirmationGranted = true;

    const callInfo = await getCallDetails(callId, this._userAuth);
    if (this.callId && `${this.callId}` === `${callId}`) {
      if (callInfo.status && callInfo.status.toLowerCase() === 'active' && this.waitData) {
        this.props.getCallInfo(callInfo);
        const { consultant = {}, consulted = {}, callerId } = callInfo;
        const person = consultant.id === callerId ? { ...consultant } : { ...consulted };

        this._calleeId = person.id;
        this._calleeJID = from;
        // this._calleeJID = generateJID(person.id, true);

        this.props.openComModal(consts.CALL, person, false, true);
        this.props.changeCallRingtoneStatus();
        this._crossTabEmitter && this._crossTabEmitter.emit(
          CROSS_TAB_RINGING_EVENT,
          { callId, currentUserJID: this._curUserJID },
        );
        this.persons[from] = `${person.name} ${person.lastName}`;
        if (!document.hasFocus()) {
          this.props.createNotification('Incoming call', this.persons[from], 40000);
        }
        (() => {
          if (!this.grantedWasSent) {
            this.grantedWasSent = true;
            this.msgGenSend(this._curUserJID, this._calleeJID, 'vcxep', 'vcxep', {
              type: 'granted',
              callid: callId,
            },
            undefined,
            true);
          }
        })();

        this.connectionQualityMonitor.resetStats();

        /*

        if (this.waitVideoElemsInterval) clearTimeout(this.waitVideoElemsInterval);
        this.waitVideoElemsInterval = setInterval(() => {
          // eslint-disable-next-line no-unused-expressions
          (this.videoInput && this.videoOutput && this.audioInput) && (
            clearInterval(this.waitVideoElemsInterval),
            (() => {
              if (!this.webRtcPeers[from]) {
                this.answering = true;
                this.rescanMediaDevices().then(() => {
                  this.webRtcPeers[from] = createPeerConnection(this._curUserJID, from, this.msgGenSend,
                    {
                      ...rtcOptions,
                      mediaConstraints: { ...this.mediaConstraints },
                      localVideo: this.videoOutput,
                      remoteVideo: this.videoInput,
                      remoteAudio: this.audioInput,
                      onicecandidate: onIceCandidate(this._curUserJID, from, cInfo.id || callId, this.msgGenSend),
                      onnegotiationneeded: () => this.offerRequest(),
                    },
                    cInfo);

                  this.setPeerStateControl(this.webRtcPeers[from].peerConnection);

                  this.addAccumulatedRemoteCandidatesToPeerConnection(this.webRtcPeers[from], from);

                  Logger.logWebRTC(
                    __FILE__,
                    __METHOD__,
                    `Diagnostics | Local HTML elements for the audio and video streams became available. WebRTC peer connection to ${from} created.`,
                  );
                }).catch((err) => this.failCall(err));
              }
            })(),
            this.waitVideoElemsInterval = null
          );
        }, 100);
        */
      }
    }
  }

  rescanMediaDevices = async (forcePermissions = false) => {
    if (forcePermissions) {
      // This is just to trigger the permissions check so we don't care which exactly
      // audio input device is chosen by the browser.
      const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStream.getTracks().forEach((track) => {
        track.stop();
        mediaStream.removeTrack(track);
      });
    }

    this.mediaConstraints = await getMediaConstraints();
  }

  sendPresence(statusText = 'Available', show = null, priority = null) {
    const __METHOD__ = 'Connection::sendPresence';

    const id = this.connection.getUniqueId('presence');
    const m = $pres({ from: this._curUserJID, id });

    if (statusText) {
      m.c('status', {}).t(statusText);
      m.up();
    }

    if (priority) {
      m.c('priority', {}).t(priority.toString());
      m.up();
    }

    if (show) {
      m.c('show', {}).t(show);
    }

    try {
      if (this.connection.connected) {
        this.connection.send(m);
      } else {
        Logger.logXMPPClient(
          __FILE__,
          __METHOD__,
          'No connection available to send presence',
          LogLevel.Warning,
        );
      }
    } catch (err) {
      Logger.logXMPPClient(
        __FILE__,
        __METHOD__,
        'Failed to send the presence',
        LogLevel.Error,
        err,
      );
    }
  }

  msgGenSend = (from, to, type, elemType, elemOptions = {}, body, forceAcknowledgement = false, useStanzaId = null) => {
    const __METHOD__ = 'Connection::msgGenSend';

    const id = useStanzaId || this.connection.getUniqueId(this.props.uuid.toUpperCase());
    const m = body
      ? $msg({
        from, to, type, id,
      }).c(elemType, elemOptions).t(body)
      : $msg({
        from, to, type, id,
      }).c(elemType, elemOptions);

    this.unacknowledgedStanzas[id] = m;
    this.unacknowledgedStanzaRetryCounts[id] = 0;

    if (type === 'vcxep' || elemType === 'vcxep') {
      Logger.logSignaling(__FILE__, __METHOD__, `Outgoing | ${elemOptions.type}`, LogLevel.Info, elemOptions);
    }

    try {
      if (this.connection.connected) {
        this.connection.send(m);
      } else {
        Logger.logXMPPClient(
          __FILE__,
          __METHOD__,
          'No connection available to send the generic XMPP message',
          LogLevel.Warning,
        );
      }
    } catch (err) {
      Logger.logXMPPClient(
        __FILE__,
        __METHOD__,
        'Failed to send the generic XMPP message',
        LogLevel.Error,
        err,
      );
    }

    this.requestAcknowledgementForSentStanzas(forceAcknowledgement);
  }

  sendMessage = (message, userID, isPro, senderName, thread) => {
    const __METHOD__ = 'Connection::sendMessage';

    Logger.logChat(__FILE__, __METHOD__, 'Sending a chat message');

    const id = uuidv1();
    const timestamp = (getTime(new Date()) + this.offset) / 1000;
    let typeFile = '';
    let msg = null;
    if (message.attachment) {
      if (message.attachment.type === 'video/mp4') {
        typeFile = 'video';
      } else if (message.attachment.type === 'application/pdf') {
        typeFile = 'document';
      } else if (message.attachment.type === 'image/jpeg' || message.attachment.type === 'image/png' || message.attachment.type === 'image/jpg') {
        typeFile = 'image';
      } else if (message.attachment.type === 'contact') {
        typeFile = 'contact';
      }
      if (typeFile === 'contact') {
        msg = {
          text: message.text,
          senderName,
          attachment: message.attachment,
        };
      } else {
        const documentType = message.attachment.type.split('/')[1];
        msg = {
          text: message.text,
          senderName,
          attachment: {
            id: message.attachment.id,
            previewId: message.attachment.id,
            type: typeFile,
            name: message.attachment.name,
            documentType,
          },
        };
      }
    } else {
      msg = {
        text: message.text,
        senderName,
      };
    }

    const to = generateJID(userID, true);
    this.props.setMsg(userID, isPro, to, {
      ...msg,
      id,
      timestamp: +timestamp.toFixed(0),
      status: statuses.SENT,
    }, true);
    const m = $msg({ to, type: 'chat', id })
      .c('body')
      .t(JSON.stringify(msg));
    m.up().c('thread').t(thread);
    m.up().c('markable', { xmlns: 'urn:xmpp:chat-markers:0' });
    // this.connection.send(m);
    this.unacknowledgedStanzas[id] = m;
    this.unacknowledgedStanzaRetryCounts[id] = 0;
    try {
      if (this.connection.connected) {
        this.connection.send(m);
      } else {
        Logger.logXMPPClient(
          __FILE__,
          __METHOD__,
          'No connection available to send the chat message',
          LogLevel.Warning,
        );
      }
    } catch (err) {
      Logger.logXMPPClient(
        __FILE__,
        __METHOD__,
        'Failed to send the chat message',
        LogLevel.Error,
        err,
      );
    }
    this[`failed-${id}-timeout`] = setTimeout(() => {
      this.props.updateMsgStatus(to, id, statuses.FAILED);
    }, 10000);

    this.requestAcknowledgementForSentStanzas();
  }

  sentSuccessfully = (msg) => {
    const __METHOD__ = 'Connection::sentSuccessfully';
    Logger.logSignaling(
      __FILE__,
      __METHOD__,
      'Successfully sent a message',
      LogLevel.Info,
      msg,
    );
  }

  sentError = (...args) => {
    const __METHOD__ = 'Connection::sentError';
    Logger.logSignaling(
      __FILE__,
      __METHOD__,
      'Failed to send a message',
      LogLevel.Error,
      { ...args },
    );
  }

  getServerTime = () => {
    const __METHOD__ = 'Connection::getServerTime';

    const id = this.connection.getUniqueId('time_1');

    const m = $iq({
      id, to: XMPP_DOMAIN, type: 'get', from: this._curUserJID,
    }).c('time', { xmlns: 'urn:xmpp:time' });

    try {
      if (this.connection.connected) {
        this.connection.send(m);
      } else {
        Logger.logXMPPClient(
          __FILE__,
          __METHOD__,
          'No connection available to request time from the server',
          LogLevel.Warning,
        );
      }
    } catch (err) {
      Logger.logXMPPClient(
        __FILE__,
        __METHOD__,
        'Failed to request the time from server',
        LogLevel.Error,
        err,
      );
    }
  }

  synchronize = () => {
    const __METHOD__ = 'Connection::synchronize';

    const id = this.connection.getUniqueId('enable1');

    const m = $iq({ id, type: 'set', from: this._curUserJID })
      .c('enable', { xmlns: 'urn:xmpp:carbons:2' });
    try {
      if (this.connection.connected) {
        this.connection.send(m);
      } else {
        Logger.logXMPPClient(
          __FILE__,
          __METHOD__,
          'No connection available to enable message carbons',
          LogLevel.Warning,
        );
      }
    } catch (err) {
      Logger.logXMPPClient(
        __FILE__,
        __METHOD__,
        'Failed to enable message carbons',
        LogLevel.Error,
        err,
      );
    }
    // this.connection.send(m1);
  }

  getChatMessages = (clientId, proId, offset = 0, order = 'DESC') => {
    const __METHOD__ = 'Connection::getChatMessages';

    const id = this.connection.getUniqueId(`history:${clientId}`);
    const historyRequestKey = `${clientId}-${offset}-${order}`;

    if (historyRequestKey in this.messageHistoryIQIds) {
      // We have a potentially unsent chat history request for the same opponent
      // with the same parameters. We discard the old request since
      // there's no point in re-sending several identical chat history
      // request.
      const prevIQStanzaId = this.messageHistoryIQIds[historyRequestKey];
      delete this.unacknowledgedStanzas[prevIQStanzaId];
      delete this.unacknowledgedStanzaRetryCounts[prevIQStanzaId];
    }

    const m = $iq({ id, to: XMPP_DOMAIN, type: 'get' })
      .c('history', {
        count: consts.MES_HISTORY_SIZE, offset, order, to: clientId,
      });
      // .t(`${clientId}-${proId}`);

    this.unacknowledgedStanzas[id] = m;
    this.unacknowledgedStanzaRetryCounts[id] = 0;
    this.messageHistoryIQIds[historyRequestKey] = id;

    try {
      if (this.connection.connected) {
        this.connection.send(m);
      } else {
        Logger.logXMPPClient(
          __FILE__,
          __METHOD__,
          'No connection available to request message history',
          LogLevel.Warning,
        );
      }
    } catch (err) {
      Logger.logXMPPClient(
        __FILE__,
        __METHOD__,
        'Failed to request message history',
        LogLevel.Error,
        err,
      );
    }
  }

  markChatMessage = (from, to, id, thread, markType) => {
    const __METHOD__ = 'Connection::markChatMessage';

    const envelopeMessageId = this.connection.getUniqueId('chat-marker');
    const m = $msg({
      from, to, type: 'chat_status', id: envelopeMessageId,
    }).c('thread').t(thread);
    m.up().c(markType, { xmlns: 'urn:xmpp:chat-markers:0', id });

    this.unacknowledgedStanzas[envelopeMessageId] = m;
    this.unacknowledgedStanzaRetryCounts[envelopeMessageId] = 0;
    try {
      if (this.connection.connected) {
        this.connection.send(m);
      } else {
        Logger.logXMPPClient(
          __FILE__,
          __METHOD__,
          'No connection available to mark chat messages',
          LogLevel.Warning,
        );
      }
    } catch (err) {
      Logger.logXMPPClient(
        __FILE__,
        __METHOD__,
        'Failed to set a chat message status',
        LogLevel.Error,
        err,
      );
    }

    this.requestAcknowledgementForSentStanzas();
  }

  setVideoElements = (videoOutput, videoInput, _audioInput, _localStream) => {
    const __METHOD__ = 'Connection::setVideoElements';

    Logger.logWithTag(
      __FILE__,
      __METHOD__,
      Logger.TAG_WEBRTC,
      'Video elements set',
      LogLevel.Info,
      { videoOutput, videoInput },
      true,
    );

    this.videoOutput = videoOutput;
    this.videoInput = videoInput;
    this.audioInput = this.props.audioInput;

    // if (localStream) this.localStream = localStream;

    if (this.callId && this.canPlayIncomingVideo()) {
      this.playIncomingVideoStream(this._calleeJID);
    }

    if (this.callId && this.callState === CallState.ACTIVE && this.localStream && this.localStream.getVideoTracks().length > 0) {
      this.playOutgoingVideoStream();
    }
  }

  callRequest = (userID, callInfo, callback = () => {}) => {
    const __METHOD__ = 'Connection::callRequest';

    this.callWasRejected = false;
    this.callFailed = false;
    this.amICallee = false;
    this._calleeId = userID;
    this._calleeJID = generateJID(userID, true);
    this.callInitiatorJID = null;
    this.setAutoReject(userID);
    callback(this.callTimeBeep);
    this.callId = callInfo.callId;
    this.incomingVideoEnabled = false;
    this.isOutgoingScreenSharingMuted = true;
    this.isOutgoingVideoMuted = true;
    this.isOutgoingAudioMuted = true;
    this.callRequestWasAcknowledged = false;
    this.webRtcPeers = {};
    this.candidates = [];

    Logger.logSignaling(
      __FILE__,
      __METHOD__,
      'Diagnostics | Placing a call',
      LogLevel.Info,
      JSON.stringify(callInfo),
    );

    this.callState = CallState.INVITED;

    this.msgGenSend(
      this._curUserJID,
      this._calleeJID,
      'vcxep',
      'vcxep',
      {
        type: 'request',
        callid: callInfo.callId,
        avtime: callInfo.avTime,
        signalingProtocol: MAX_SUPPORTED_SIGNALING_PROTOCOL,
      },
      undefined,
      true,
    );

    this.events.emit(OUTGOING_CALL_STARTED_EVENT, callInfo.callId);
  }

  rejectCall = (isBusy = false, cInf = {}) => {
    const __METHOD__ = 'Connection::rejectCall';

    this.stopWaitingVideoElements();
    this.callWasRejected = true;
    Logger.logSignaling(
      __FILE__,
      __METHOD__,
      `Diagnostics | Rejecting the call; isBusy: ${isBusy}`,
    );
    this.undoAutoReject();
    this.undoProAutoDecline();
    let cInfo = this.props.getCInfo() || {};
    cInfo = cInfo.callerId ? cInfo : cInf;
    const type = (!cInfo.callerId || cInfo.callerId === this._curUserId) ? 'callerDeclined' : 'receiverDeclined';
    const rejectionMessageRecipientJID = this._calleeJID || generateJID(cInfo.person, true);
    const rejectionMessageSenderJID = this._curUserJID;
    const rejectedCallId = cInfo.callId;

    this.callState = type === 'callerDeclined' ? CallState.CLIENT_DECLINE : CallState.PRO_DECLINE;

    this._calleeId = '';
    this._calleeJID = '';
    this.callInitiatorJID = null;
    if (isBusy === true) {
      this.props.caleeIsBusy();
    } else {
      this.props.closeComModal();
    }
    this.callId = undefined;
    this.callRequestWasAcknowledged = undefined;
    this.stopCommunication();

    const sendRejectionMessage = () => this.msgGenSend(
      rejectionMessageSenderJID,
      rejectionMessageRecipientJID,
      'vcxep',
      'vcxep',
      { type, callid: rejectedCallId },
      undefined,
      true,
    );

    if (this.callState === CallState.CLIENT_DECLINE) {
      sendRejectionMessage();
    } else {
      this.checkIfCallAnsweredOnAnotherDevice(rejectionMessageRecipientJID, this._curUserJID, cInfo.callId, this._userAuth)
        .then((isAnswered) => {
          if (!isAnswered) {
            sendRejectionMessage();
          } else {
            Logger.logSignaling(
              __FILE__,
              __METHOD__,
              'Decision | NOT sending "receiverDeclined" as the call is reported either as an active one, or has already finished',
              LogLevel.Info,
            );
          }
        }).catch((err) => {
          Logger.logSignaling(
            __FILE__,
            __METHOD__,
            `Diagnostics | Failed to check if the call with callId=${rejectedCallId} was answered on another Pro's device`,
            LogLevel.Warning,
            err,
          );
        });
    }
  }

  failCall = (reason, amICalleeOverride) => {
    const __METHOD__ = 'Connection::failCall';

    if (this.callState === CallState.FAILED) {
      return;
    }

    this.stopWaitingVideoElements();
    this.callFailed = true;

    Logger.logSignaling(
      __FILE__,
      __METHOD__,
      `Diagnostics | Aborting the call as failed with reason: ${reason}`,
    );

    this.undoAutoReject();
    this.undoProAutoDecline();

    const cInfo = this.props.getCInfo() || { callId: this.callId };
    const amICallee = amICalleeOverride || this.amICallee;

    this.callState = CallState.FAILED;

    const thisParty = this._curUserJID;
    const otherParty = this._calleeJID;
    const failedCallId = cInfo.callId;

    const notifyTheOtherPartyOfFailedCall = () => {
      Logger.logSignaling(
        __FILE__,
        __METHOD__,
        `Diagnostics | Notifying that the call has failed with reason: ${reason}`,
      );

      this.msgGenSend(thisParty, otherParty, 'vcxep', 'vcxep', { type: 'failed', callid: failedCallId }, reason);

      // We need to follow up the "failed" message by sending "callerDeclined"
      // in the Client (caller) role because otherwise ejabberd will keep the call
      // as an active one.
      if (!amICallee) {
        this.msgGenSend(
          thisParty,
          generateJID(otherParty, true),
          'vcxep',
          'vcxep',
          { type: 'callerDeclined', callid: failedCallId },
          undefined,
          true,
        );
      }
    };

    if (cInfo.callId) {
      notifyTheOtherPartyOfFailedCall();
    }

    this.callId = undefined;
    this.callRequestWasAcknowledged = undefined;
    this._calleeId = '';
    this._calleeJID = '';
    this.callInitiatorJID = null;

    this.stopCommunication();
    this.events.emit(CALL_FAILED_EVENT, amICallee, reason);
  }

  callHasBeenAcceptedOnProsDevice = () => !this.amICallee
    && (this.callState === CallState.ACCEPTED
        || this.callState === CallState.ACTIVE);

  reqGranted = () => {
    const __METHOD__ = 'Connection::reqGranted';

    this.props.changeCallRingtoneStatus(false);

    // We need a rescan because the user could have plugged in an audio device
    // after the initial scan.
    this.rescanMediaDevices(true).then(() => {
      if (!this.wasAccepted) {
        this.wasAccepted = true;
        const cInfo = this.props.getCInfo() || {};

        this.callState = CallState.ACCEPTED;
        this.msgGenSend(this._curUserJID, this._calleeJID, 'vcxep', 'vcxep', { type: 'accept', callid: cInfo.callId });

        this.callState = CallState.ACTIVE;
        this.muteAudio(false);
        this.msgGenSend(this._curUserJID, this._calleeJID, 'vcxep', 'vcxep', { type: 'speaking', callid: cInfo.callId });

        this.requestAcknowledgementForSentStanzas(true);

        this.events.emit(INCOMING_CALL_STARTED_EVENT, cInfo.callId);
      }
    }).catch((err) => {
      this.failCall(this.tryGetMediaCaptureErrorReason(err), true);

      Logger.logWebRTC(
        __FILE__,
        __METHOD__,
        `Unable to rescan the media devices. The exact error is ${err.name}: ${err.message}`,
      );
    });
  }

  stopCommunication = (closeModal = true) => {
    const { callId } = this;

    try {
      this.presenceConfirmationGranted = false;

      this.props.changeReconnectionStatus(false);
      this.waitData = false;
      clearInterval(this.restoreIceInterval);
      clearTimeout(this.connectionLostTimeout);
      if (this.connectionStatsTimer) {
        clearInterval(this.connectionStatsTimer);
        this.connectionStatsTimer = undefined;
      }

      this.connectionLostTimeout = null;

      if (closeModal) {
        this.props.closeComModal();
      }
      if (this.waitVideoElemsInterval) clearTimeout(this.waitVideoElemsInterval);
      this.props.changeCallRingtoneStatus(false);
      this.amICallee = false;
      this.disableVideo();
      this.stopLocalStream();
      this.candidates = [];
      this.grantedWasSent = undefined;
      this.wasAccepted = undefined;
      this.videoOutput = undefined;
      this.videoInput = undefined;
      this.audioInput = undefined;
      this.callId = undefined;
      this.callInitiatorJID = null;
      this.callRequestWasAcknowledged = undefined;
      this.callProtocolVersion = undefined;
      this.persons = {};

      this.videoSender = null;
      this.answering = false;
      this.incomingVideoEnabled = false;
      this.isOutgoingScreenSharingMuted = true;
      this.isOutgoingVideoMuted = true;
      this.isOutgoingAudioMuted = true;

      Object.keys(this.webRtcPeers).forEach((key) => {
        if (this.webRtcPeers[key]) {
          try {
            this.webRtcPeers[key].dispose();
          // eslint-disable-next-line no-empty
          } catch (_disposeErr) { }
        }
      });

      this.webRtcPeers = {};
    } finally {
      this.events.emit(CALL_FINISHED_EVENT, callId);
    }
  }

  stopLocalStream = () => {
    const __METHOD__ = 'Connection::stopLocalStream';

    Logger.logWebRTC(
      __FILE__,
      __METHOD__,
      'Stop local stream',
    );

    if (this.audioInput && this.audioInput.srcObject) {
      this.audioInput.srcObject.getTracks().forEach(trackStop);
      this.audioInput.srcObject = null;
    }

    if (this.localStream) {
      this.localStream.getTracks().forEach(trackStop);
      this.localStream = null;
    }

    if (this.webRtcPeers[this._calleeJID]) {
      this.webRtcPeers[this._calleeJID].peerConnection.getLocalStreams().forEach((stream) => {
        stream.getTracks().forEach(trackStop);
      });
    }

    if (this.webRtcPeers[this._calleeJID]) {
      this.webRtcPeers[this._calleeJID].peerConnection.getSenders().forEach(
        (sender) => {
          if (sender.track) {
            // eslint-disable-next-line no-param-reassign
            sender.track.enabled = false;
            sender.track.stop();
          }
        },
      );
    }
    if (this.webRtcPeers[this._calleeJID]) this.webRtcPeers[this._calleeJID].peerConnection.close();
  };

  rtcConnectionLost = () => {
    const __METHOD__ = 'Connection::rtcConnectionLost';

    if (this.connectionLostTimeout) {
      return;
    }

    this.connectionLostTimeout = setTimeout(() => {
      this.props.undoAutoFinishNotif();

      clearInterval(this.restoreIceInterval);

      this.undoAutoFinish();

      const cInfo = this.props.getCInfo() || {};

      this.callState = CallState.DISCONNECTED;

      try {
        this.msgGenSend(this._curUserJID, this._calleeJID, 'vcxep', 'vcxep', { type: 'rtcConnectionLost', callid: cInfo.callId }, undefined, true);
      } catch (error) {
        Logger.logSignaling(
          __FILE__,
          __METHOD__,
          'Cannot notify eJabberd about WebRTC disconnection!',
          LogLevel.Error,
          error,
        );
      }

      this.stopCommunication();
    }, DISCONNECT_TIMEOUT_MS);
  };

  finishCall = (forciblyFinished = false) => {
    this.stopCommunication();
    this.props.undoAutoFinishNotif();
    this.undoAutoFinish();

    const cInfo = this.props.getCInfo() || {};

    this.callState = forciblyFinished ? CallState.BREAK : CallState.SUCCEEDED;

    this.msgGenSend(this._curUserJID, this._calleeJID, 'vcxep', 'vcxep', { type: 'finished', callid: cInfo.callId }, undefined, true);
  }

  gracefullyCloseCallOnLogout = () => {
    const __METHOD__ = 'Connection::gracefullyCloseCallOnLogout';

    if (this.callId) {
      if (this.callState === CallState.ACTIVE) {
        Logger.logSignaling(__FILE__, __METHOD__, 'The user has logged out while a call is in progress - finishing the call');
        this.finishCall();
      } else if (this.callState === CallState.INVITED || this.callState === CallState.ACCEPTED || this.callState === CallState.PRO_TIMEOUT) {
        const callerJID = this.amICallee
          ? this._calleeJID || this.callInitiatorJID
          : this._calleeJID;
        if (!callerJID) {
          return;
        }

        Logger.logSignaling(__FILE__, __METHOD__, 'The user has logged out while a call is being connected - rejecting the call');
        this.rejectCall(
          false,
          { callerId: Strophe.getNodeFromJid(callerJID), person: callerJID },
        );
      }
    }
  }

  muteAudio = (isMuted) => {
    const __METHOD__ = 'Connection::muteAudio';

    this.isOutgoingAudioMuted = isMuted;

    if (this.webRtcPeers[this._calleeJID]) {
      const senders = this.webRtcPeers[this._calleeJID].peerConnection.getSenders();
      if (!senders.length) {
        Logger.logWebRTC(
          __FILE__,
          __METHOD__,
          `A WebRTC peer connection for the remote party with JID ${this._calleeJID} does not have any senders`,
          LogLevel.Warning,
        );
      }

      let atLeastOneAudioTrackSenderFound = false;
      senders.forEach((element) => {
        if (element.track && element.track.kind === 'audio') {
          // eslint-disable-next-line no-param-reassign
          element.track.enabled = !isMuted;
          atLeastOneAudioTrackSenderFound = true;
        }
      });
      if (!atLeastOneAudioTrackSenderFound) {
        Logger.logWebRTC(
          __FILE__,
          __METHOD__,
          `A WebRTC peer connection for the remote party with JID ${this._calleeJID} does not have any senders with an audio track`,
          LogLevel.Warning,
        );
      }

      if (this.localStream) {
        let atLeastOneLocalAudioTrackFound = false;

        this.localStream.getAudioTracks().forEach((track) => {
          // eslint-disable-next-line no-param-reassign
          track.enabled = !isMuted;
          atLeastOneLocalAudioTrackFound = true;
        });

        if (!atLeastOneLocalAudioTrackFound) {
          Logger.logWebRTC(
            __FILE__,
            __METHOD__,
            'The local stream does not contain any audio tracks',
            LogLevel.Warning,
          );
        }
      } else {
        Logger.logWebRTC(
          __FILE__,
          __METHOD__,
          'There is no local stream available',
          LogLevel.Warning,
        );
      }
    } else {
      Logger.logWebRTC(
        __FILE__,
        __METHOD__,
        `A WebRTC peer connection for the remote party with JID ${this._calleeJID} does not exist`,
        LogLevel.Error,
      );
    }
  }

  muteVideo = (isMuted, screenSharingMuted, callback) => {
    if (!isMuted && !screenSharingMuted) {
      this.isScreenSharingListener = false;
      this.disableScreenSharing(callback, false);
    }
    const cInfo = this.props.getCInfo() || {};
    const type = isMuted ? 'dis_video' : 'en_video';
    const options = { type, callid: cInfo.callId };
    this.updateVideoStatus(isMuted);

    if (JSON.stringify(this.videoCallOptions) !== JSON.stringify(options)) {
      this.msgGenSend(this._curUserJID, this._calleeJID, 'vcxep', 'vcxep', options, undefined, true);
      this.videoCallOptions = options;
    }
  }

  muteOpponentAudio = (isMuted) => {
    this.props.audioInput.muted = isMuted;
  }

  isOpponentAudioMuted = () => this.props.audioInput.muted;

  playIncomingVideoStream = (opponentJID) => {
    const __METHOD__ = 'Connection::playIncomingVideoStream';

    if (this.webRtcPeers[opponentJID] && this.videoInput) {
      const remoteStreams = this.webRtcPeers[opponentJID].peerConnection.getRemoteStreams();
      const playableVideoStream = remoteStreams.find(
        (stream) => stream.getVideoTracks().length && stream.active,
      );

      if (playableVideoStream) {
        this.videoInput.srcObject = playableVideoStream;
        this.videoInput.muted = true;
        this.videoInput.volume = 0;
        this.videoInput.load();
        this.videoInput.play().catch((e) => {
          if (e instanceof DOMException && e.name === 'AbortError') {
            Logger.logWebRTC(
              __FILE__,
              __METHOD__,
              'A new incoming video stream replaced an existing video stream',
              LogLevel.Warning,
            );
          } else {
            Logger.logWebRTC(
              __FILE__,
              __METHOD__,
              `Failed to play the incoming video stream. An error occurred: ${e.name || 'unknown error'} with message '${e.message}'`,
              LogLevel.Warning,
            );
          }
        });
        Logger.logWebRTC(__FILE__, __METHOD__, 'Incoming video stream already present - connecting to video playback');
      } else {
        Logger.logWebRTC(__FILE__, __METHOD__, 'Incoming video stream not yet present - waiting for an incoming video track');
      }
    }
  }

  playOutgoingVideoStream = () => {
    if (!this.videoOutput || !this.localStream) {
      return;
    }

    this.videoOutput.srcObject = null;
    this.videoOutput.srcObject = this.localStream;

    setTimeout(() => {
      if (this.videoOutput.paused) {
        this.videoOutput.play().catch((_err) => { });
      }
    }, VIDEO_PLAYING_TIMEOUT_MS);
  }

  updateVideoStatus = (muted) => {
    if (!muted) {
      this.enableVideo();
    } else {
      this.disableVideo();
    }
  }

  enableVideo = () => {
    const __METHOD__ = 'Connection::enableVideo';
    const __CALLBACK_METHOD_GET_TRACKS__ = 'Connection::enableVideo::getVideoTracksCallback';
    const __CALLBACK_METHOD_REPLACE_TRACK__ = 'Connection::enableVideo::replaceTrackCallback';
    const __CALLBACK_METHOD_GET_USER_MEDIA__ = 'Connection::enableVideo::getUserMediaCallback';

    this.isOutgoingVideoMuted = false;

    Logger.logWebRTC(
      __FILE__,
      __METHOD__,
      'Start video',
    );
    navigator.mediaDevices.getUserMedia({ video: true }).then((videoStream) => {
      const videoTracks = videoStream.getVideoTracks();
      if (videoTracks.length > 0) {
        Logger.logWebRTC(
          __FILE__,
          __CALLBACK_METHOD_GET_TRACKS__,
          `Using video device: ${videoTracks[0].label}`,
        );
      } else {
        Logger.logWebRTC(
          __FILE__,
          __CALLBACK_METHOD_GET_TRACKS__,
          'The captured stream does not contain any video tracks',
          LogLevel.Error,
        );

        return;
      }

      this.localStream.addTrack(videoTracks[0]);

      const remotePeer = this.webRtcPeers[this._calleeJID];

      if (!this.videoSender) {
        const localStream = remotePeer.getLocalStream();
        this.videoSender = remotePeer.peerConnection.addTrack(videoTracks[0], localStream);
        this.videoSender.track.enabled = true;
      } else {
        this.videoSender.replaceTrack(videoTracks[0]).catch((error) => {
          Logger.logWebRTC(
            __FILE__,
            __CALLBACK_METHOD_REPLACE_TRACK__,
            'Failed to replace a video track',
            LogLevel.Error,
            error,
          );
        }).then(() => {
          this.videoSender.track.enabled = true;
        });
      }

      this.playOutgoingVideoStream();
    })
      .catch((e) => {
        Logger.logWebRTC(
          __FILE__,
          __CALLBACK_METHOD_GET_USER_MEDIA__,
          'Failed to acquire user media',
          LogLevel.Error,
          e,
        );
      });
  };

  disableVideo = () => {
    const __METHOD__ = 'Connection::disableVideo';

    Logger.logWebRTC(
      __FILE__,
      __METHOD__,
      'End Video/Screen Sharing Stream',
    );
    this.isOutgoingVideoMuted = true;

    if (this.videoSender && this.videoSender.track) {
      this.videoSender.track.enabled = false;
      this.videoSender.track.stop();
    }
    if (this.localStream) {
      this.localStream.getVideoTracks().forEach((track) => {
        trackStop(track);
        this.localStream.removeTrack(track);
        Logger.logWebRTC(
          __FILE__,
          __METHOD__,
          'Stopped and removed the video track',
          LogLevel.Info,
          track,
        );
      });
    }
  }

  updateScreenSharingMsg = (isSharing) => {
    const cInfo = this.props.getCInfo() || {};
    const type = isSharing ? 'dis_video' : 'en_video';
    const options = { type, callid: cInfo.callId };

    if (JSON.stringify(this.videoCallOptions) !== JSON.stringify(options)) {
      this.msgGenSend(this._curUserJID, this._calleeJID, 'vcxep', 'vcxep', options, undefined, true);
      this.videoCallOptions = options;
    }
  }

  updateScreenSharingStatus = (isSharingMuted, isVideoMuted, callback) => {
    if (isSharingMuted) {
      this.enableScreenSharing(isVideoMuted, callback);
    } else {
      this.isScreenSharingListener = false;
      this.disableScreenSharing(callback);
    }
  }

  addStreamStopListener = (stream, callback) => {
    const emptyFn = () => {};
    let callbackFn = typeof callback === 'function' ? callback : emptyFn;
    stream.addEventListener('ended', () => {
      callbackFn();
      callbackFn = emptyFn;
    }, false);
    stream.addEventListener('inactive', () => {
      callbackFn();
      callbackFn = emptyFn;
    }, false);
    stream.getTracks().forEach((track) => {
      track.addEventListener('ended', () => {
        callbackFn();
        callbackFn = emptyFn;
      }, false);
      track.addEventListener('inactive', () => {
        callbackFn();
        callbackFn = emptyFn;
      }, false);
    });
  }

  enableScreenSharing = (isVideoMuted, callback) => {
    const __METHOD__ = 'Connection::enableScreenSharing';
    const __CALLBACK_METHOD_ON_GETTING_STREAM__ = 'Connection::enableScreenSharing::onGettingStreamCallback';
    const __CALLBACK_METHOD_GET_DISPLAY_MEDIA__ = 'Connection::enableScreenSharing::onGetDisplayMediaCallback';
    const __CALLBACK_METHOD_REPLACE_TRACK__ = 'Connection::enableScreenSharing::replaceTrackCallback';

    this.isOutgoingScreenSharingMuted = false;

    let isScreenSharingMuted = true;
    if (navigator.getDisplayMedia || navigator.mediaDevices.getDisplayMedia) {
      const onGettingSteam = (videoStream) => {
        const videoTracks = videoStream.getVideoTracks();

        this.addStreamStopListener(videoStream, () => {
          this.isScreenSharingListener && this.disableScreenSharing(callback);
          this.isScreenSharingListener = true;
        });
        if (videoTracks.length > 0) {
          Logger.logWebRTC(
            __FILE__,
            __CALLBACK_METHOD_ON_GETTING_STREAM__,
            `Using Screen Sharing device: ${videoTracks[0].label}`,
          );
        }
        const localStream = this.webRtcPeers[this._calleeJID].getLocalStream();
        this.localStream.addTrack(videoTracks[0]);
        if (!this.videoSender) {
          this.videoSender = this.webRtcPeers[this._calleeJID].peerConnection.addTrack(videoTracks[0], localStream);
          this.videoSender.track.enabled = true;
        } else {
          this.videoSender.replaceTrack(videoTracks[0]).catch((error) => {
            Logger.logWebRTC(
              __FILE__,
              __CALLBACK_METHOD_REPLACE_TRACK__,
              'Failed to replace a screen sharing track',
              LogLevel.Error,
              error,
            );
          }).then(() => {
            this.videoSender.track.enabled = true;
          });
        }

        this.playOutgoingVideoStream();
      };

      if (navigator.mediaDevices.getDisplayMedia) {
        navigator.mediaDevices.getDisplayMedia({ video: true }).then((stream) => {
          !isVideoMuted && this.disableVideo();
          Logger.logWebRTC(
            __FILE__,
            __CALLBACK_METHOD_GET_DISPLAY_MEDIA__,
            'Start Screen Sharing with mediaDevices.getDisplayMedia',
          );
          isScreenSharingMuted = false;
          this.updateScreenSharingMsg(isScreenSharingMuted);
          onGettingSteam(stream);
          if (typeof callback === 'function') {
            callback(isScreenSharingMuted);
          }
        }).catch((e) => {
          Logger.logWebRTC(
            __FILE__,
            __CALLBACK_METHOD_GET_DISPLAY_MEDIA__,
            'Failed to acquire the display media for screen sharing with mediaDevices.getDisplayMedia',
            LogLevel.Error,
            e,
          );

          if ((e instanceof DOMException) && e.name === 'NotAllowedError') {
            this.events.emit(SCREEN_SHARING_PERMISSION_DENIED_EVENT, e.message);
          }
        });
      } else if (navigator.getDisplayMedia) {
        navigator.getDisplayMedia({ video: true }).then((stream) => {
          !isVideoMuted && this.disableVideo();
          Logger.logWebRTC(
            __FILE__,
            __CALLBACK_METHOD_GET_DISPLAY_MEDIA__,
            'Start Screen Sharing with getDisplayMedia',
          );
          isScreenSharingMuted = false;
          this.updateScreenSharingMsg(isScreenSharingMuted);
          onGettingSteam(stream);
          if (typeof callback === 'function') {
            callback(isScreenSharingMuted);
          }
        }).catch((e) => {
          Logger.logWebRTC(
            __FILE__,
            __CALLBACK_METHOD_GET_DISPLAY_MEDIA__,
            'Failed to acquire the display media for screen sharing with getDisplayMedia',
            LogLevel.Error,
            e,
          );

          if ((e instanceof DOMException) && e.name === 'NotAllowedError') {
            this.events.emit(SCREEN_SHARING_PERMISSION_DENIED_EVENT, e.message);
          }
        });
      }
    } else if (typeof callback === 'function') {
      Logger.logWebRTC(
        __FILE__,
        __METHOD__,
        'Neither navigator.getDisplayMedia nor navigator.mediaDevices.getDisplayMedia API available',
        LogLevel.Error,
      );
    }
  };

  disableScreenSharing = (callback, updateSteamMsg = true) => {
    this.isOutgoingScreenSharingMuted = true;
    updateSteamMsg && this.updateScreenSharingMsg(true);
    this.disableVideo();
    if (typeof callback === 'function') {
      callback(true);
    }
  }

  replaceAudioTrack = (audioStream, peerConnection) => {
    const __CALLBACK_METHOD_REPLACE_TRACK__ = 'Connection::replaceAudioTrack::replaceTrack';

    const audioTracks = audioStream.getAudioTracks();
    const stream = peerConnection.getLocalStreams()[0];
    peerConnection.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.stop();
        sender.replaceTrack(audioTracks[0], stream).then((a) => Logger.logWebRTC(
          __FILE__,
          __CALLBACK_METHOD_REPLACE_TRACK__,
          'audio replaced',
          LogLevel.Info,
          a,
        ));
      }
    });
  }

  offerRequest = () => {
    const __METHOD__ = 'Connection::offerRequest';

    const cInfo = this.props.getCInfo() || {};
    Logger.logSignaling(
      __FILE__,
      __METHOD__,
      'Diagnostics | Negotiation in progress',
    );

    if (!this.answering) {
      Logger.logSignaling(
        __FILE__,
        __METHOD__,
        'Decision | Requesting an offer SDP from the caller',
      );
      this.msgGenSend(this._curUserJID, this._calleeJID, 'vcxep', 'vcxep', { type: 'offerRequest', callid: cInfo.callId }, undefined, true);
    } else {
      Logger.logSignaling(
        __FILE__,
        __METHOD__,
        'Diagnostics | Already have an SDP offer, no need to request a new one',
      );
    }
  }

  stopWaitingVideoElements = () => {
    if (this.waitVideoElemsInterval) {
      clearTimeout(this.waitVideoElemsInterval);
      this.waitVideoElemsInterval = 0;
    }
  }

  addAccumulatedRemoteCandidatesToPeerConnection = (peerConnection, remotePartyJID) => {
    if (this.candidates.length > 0) {
      this.candidates.filter((c) => c.receivedFrom === remotePartyJID).forEach((candidate) => {
        this.addICECandidateToPeerConnection(peerConnection, candidate);
      });
      this.candidates = [];
    }
  }

  isMediaCapturePermissionError = (err) => (err instanceof DOMException) && (err.name === 'NotAllowedError');

  isNoSuitableMediaDeviceFoundError = (err) => (err instanceof DOMException) && (err.name === 'NotFoundError');

  tryGetMediaCaptureErrorReason = (err) => {
    if (this.isMediaCapturePermissionError(err)) {
      return ERR_ACCESS_TO_MEDIA_DEVICE_DENIED;
    }

    if (this.isNoSuitableMediaDeviceFoundError(err)) {
      return ERR_NO_AUDIO_DEVICE;
    }

    if (err instanceof DOMException) {
      return `${err.name}: ${err.description}`;
    }

    return `${err}`;
  }

  isCallStatusRequestSupported = (callProtocolVersion) => compareVersions(callProtocolVersion, MIN_SIGNALING_PROTOCOL_SUPPORTING_CALLSATUS_REQUEST) >= 0;

  getCallProtocolVersionFromCallRequest = (callRequestBody) => (callRequestBody.hasAttribute('signalingProtocol')
    ? callRequestBody.getAttribute('signalingProtocol')
    : MIN_SUPPORTED_SIGNALING_PROTOCOL
  ) || MIN_SUPPORTED_SIGNALING_PROTOCOL;

  getCurrentUserJID = () => this._curUserJID;

  getMaxSupportedSignalingProtocolVersion = () => MAX_SUPPORTED_SIGNALING_PROTOCOL;

  shouldForceAudioPermissionCheck = () => typeof this.mediaConstraints.audio === 'undefined'
    || typeof this.mediaConstraints.audio === 'boolean'
    || (typeof this.mediaConstraints.audio === 'object' && !this.mediaConstraints.audio.deviceId);

  canPlayCalleesAudioTrack = (from) => (this.callState === CallState.ACTIVE) && (from === this._calleeJID)

  canPlayCallerAudioTrack = () => (this.callState === CallState.ACTIVE) && (this.amICallee === true);

  shallMuteOutgoingAudio = () => (this.callState !== CallState.ACTIVE);

  canPlayIncomingVideo = () => (this.callState === CallState.ACTIVE) && this.incomingVideoEnabled;

  makePeerConnectionCreationFailedHandler = (opponentJID) => (err) => {
    // Prevent failing a call if the error callback was invoked
    // for an already disposed peer connection.
    if (err && (opponentJID in this.webRtcPeers)) {
      this.failCall(err);
    }
  };

  checkIfCallAnsweredOnAnotherDevice = async (callerJID, currentUserJID, checkedCallId, userAuth) => {
    const __METHOD__ = 'ensureCallHasNotBeenAnsweredOnAnotherDevice';
    const WAIT_TIMEOUT = 15000;
    const MAX_CALL_STATUS_CHECKS = 3;
    const EXECUTOR_API = 'api';
    const EXECUTOR_CALL_STATUS_RESPONSE = 'callStatusResponse';
    let attemptsCount = 0;

    const makeErrorStanzaHandler = (expectedStanzaId, isTimedOut, callback) => (msg) => {
      if (isTimedOut()) {
        return false;
      }

      const { id: messId } = processServerMsg(msg);
      if (messId === expectedStanzaId) {
        callback();
        return false;
      }

      return true;
    };

    const makeCallStatusResponseStanzaHandler = (expectedCallId, isTimedOut, callback) => (msg) => {
      if (isTimedOut()) {
        return false;
      }

      const { type, vcxepElems } = processServerMsg(msg);

      if (type === 'vcxep' && vcxepElems.length > 0) {
        const body = vcxepElems[0];
        const { type: callType, callId, bodyInner } = processCallMsg(body);

        if ((`${callId}` === `${expectedCallId}`) && callType === 'callStatusResponse') {
          callback(bodyInner);
          return false;
        }
        if (`${callId}` === `${expectedCallId}`
            && (callType === 'callDisconnected' || callType === 'noAnswer' || callType === 'callerDeclined')) {
          callback('outdated');
          return false;
        }
      }

      return true;
    };

    const callStatusRequestExecutor = (resolve, reject) => {
      // Make sure we operate on the same Strophe.Connection instance within a single attempt
      const xmppConnection = this.connection;

      if (!xmppConnection) {
        reject(new Error('No XMPP connection instance is available'));
      }
      const callStatusRequestMessageId = xmppConnection.getUniqueId(`callstatus-сheck-${attemptsCount}`);

      let errorStanzaHandlerRef;
      let callStatusResponseHandlerRef;

      const deleteTempStanzaHandlers = () => {
        if (errorStanzaHandlerRef) {
          xmppConnection.deleteHandler(errorStanzaHandlerRef);
          errorStanzaHandlerRef = null;
        }
        if (callStatusResponseHandlerRef) {
          xmppConnection.deleteHandler(callStatusResponseHandlerRef);
          callStatusResponseHandlerRef = null;
        }
      };

      let timedOut = false;
      const isTimedOut = () => timedOut;

      const watchdogTimer = setTimeout(() => {
        timedOut = true;
        deleteTempStanzaHandlers();
        reject(new Error('No response to "callStatusRequest" from the caller\'s device'));
      }, WAIT_TIMEOUT);

      const onCallStatusRequestFailed = () => {
        deleteTempStanzaHandlers();
        clearTimeout(watchdogTimer);
        reject(new Error('Failed to deliver the "callStatusRequest" message to the caller\'s device'));
      };

      const onCallStatusResponseReceived = (reportedCallStatus) => {
        deleteTempStanzaHandlers();
        clearTimeout(watchdogTimer);
        resolve([EXECUTOR_CALL_STATUS_RESPONSE, reportedCallStatus]);
      };

      errorStanzaHandlerRef = xmppConnection.addHandler(
        makeErrorStanzaHandler(callStatusRequestMessageId, isTimedOut, onCallStatusRequestFailed),
        null,
        'message',
        'error',
        callStatusRequestMessageId,
        callerJID,
      );
      callStatusResponseHandlerRef = xmppConnection.addHandler(
        makeCallStatusResponseStanzaHandler(checkedCallId, isTimedOut, onCallStatusResponseReceived),
        null,
        'message',
        'vcxep',
        null,
        callerJID,
      );

      if (xmppConnection.connected) {
        this.msgGenSend(
          currentUserJID,
          callerJID,
          'vcxep',
          'vcxep',
          { type: 'callStatusRequest', callid: checkedCallId },
          undefined,
          true,
          callStatusRequestMessageId,
        );
      } else {
        clearTimeout(watchdogTimer);
        deleteTempStanzaHandlers();
        reject(new Error('The XMPP connection is no longer connected'));
      }
    };

    const apiRequestExecutor = (resolve, reject) => {
      let timedOut = false;

      const watchdogTimer = setTimeout(() => {
        timedOut = true;
        reject(new Error('No response from the back-end API'));
      }, WAIT_TIMEOUT);

      getCallDetails(checkedCallId, userAuth)
        .then((callInfo) => {
          if (timedOut) {
            return;
          }

          clearTimeout(watchdogTimer);
          resolve([EXECUTOR_API, callInfo]);
        })
        .catch((apiError) => {
          if (timedOut) {
            return;
          }

          clearTimeout(watchdogTimer);
          reject(new Error(`Back-end API returned an error: ${apiError.message}`));
        });
    };

    let apiCallStatus = null;
    let callStatusResponse = null;

    for (attemptsCount = 1; attemptsCount <= MAX_CALL_STATUS_CHECKS; attemptsCount += 1) {
      const callStatusResponsePromise = new Promise(callStatusRequestExecutor);
      const apiResponsePromise = new Promise(apiRequestExecutor);

      try {
        Logger.logSignaling(__FILE__, __METHOD__, `Checking the status of the call with call ID: ${checkedCallId}, attempt #${attemptsCount}`);
        // eslint-disable-next-line no-await-in-loop
        const [whichExecutor, response] = await Promise.any([callStatusResponsePromise, apiResponsePromise]);

        switch (whichExecutor) {
          case EXECUTOR_API:
            apiCallStatus = response.status.toUpperCase();
            Logger.logSignaling(__FILE__, __METHOD__, `Got the call status from the API. Call ID: ${checkedCallId}, status: ${apiCallStatus}`);
            switch (apiCallStatus) {
              case 'INVITED':
                // The call didn't happen - safe to send 'receiverDeclined'
                return false;
              case 'ACTIVE':
                // The call status returned by the back-end API is inconclusive - let's try to wait for callStatusResponse.
                //
                // We intentionally don't handle any errors here because if callStatusResponsePromise rejects, this simply
                // means a failed attempt;
                //
                // eslint-disable-next-line no-await-in-loop
                [, callStatusResponse] = await callStatusResponsePromise;
                Logger.logSignaling(__FILE__, __METHOD__, `Got the call status from the client device within the same attempt. Call ID: ${checkedCallId}, status: ${callStatusResponse}`);
                switch (callStatusResponse) {
                  case 'active':
                    // The caller reports that the call has not been answered yet - no further checks needed;
                    return false;
                  case 'answeredElsewhere':
                    // The caller reports that the call has been answered - no further checks needed;
                    return true;
                  case 'outdated':
                    // The caller's client could have restarted and no longer knows about this call.
                    // Given that the API still considers the call as ACTIVE so the call could have happened, do not send 'receiverDeclined'
                    return true;
                  default:
                    throw new Error('Unknown call status returned in the "callStatusResponse" message');
                }
                // eslint-disable-next-line no-unreachable
                break;
              default:
                // Assume that any other call status reported by the back-end API is a "terminating" status meaning that the call is over.
                // So, if the call is reported as having been finished for one reason or another by the back-end API,
                // assume that the call was answered and stop further checks.
                return true;
            }
            // eslint-disable-next-line no-unreachable
            break;
          case EXECUTOR_CALL_STATUS_RESPONSE:
            callStatusResponse = response;
            Logger.logSignaling(__FILE__, __METHOD__, `Got the call status from the client device. Call ID: ${checkedCallId}, status: ${callStatusResponse}`);

            switch (callStatusResponse) {
              case 'active':
                // The caller reports that the call has not been answered yet - no further checks needed;
                return false;
              case 'answeredElsewhere':
                // The caller reports that the call has been answered - no further checks needed;
                return true;
              case 'outdated':
                {
                  // The caller's client could have restarted and no longer knows about this call.
                  // So let's check which status we receive from the back-end API.
                  //
                  // We intentionally don't handle any errors here because if apiResponsePromise rejects, this simply
                  // means a failed attempt;
                  //
                  // eslint-disable-next-line no-await-in-loop
                  const [, apiResponse] = await apiResponsePromise;
                  apiCallStatus = apiResponse.status.toUpperCase();
                  Logger.logSignaling(__FILE__, __METHOD__, `Got the call status from the API within the same attempt. Call ID: ${checkedCallId}, status: ${apiCallStatus}`);

                  switch (apiCallStatus) {
                    case 'ACTIVE':
                      // If the call is reported as ACTIVE by the back-end API, the call *might* have been answered (even if is disconnected now). Stopping further checks.
                      return true;
                    case 'INVITED':
                      // The call is stuck in the INVITED state - it's safe to send 'receiverDeclined'
                      return false;
                    default:
                      // Assume that any other call status reported by the back-end API is a "terminating" status meaning that the call is over.
                      // So, if the call is reported as having been finished for one reason or another by the back-end API,
                      // assume that the call was answered and stop further checks.
                      return true;
                  }
                }
                // eslint-disable-next-line no-unreachable
                break;
              default:
                throw new Error('Unknown call status returned in the "callStatusResponse" message');
            }
            // eslint-disable-next-line no-unreachable
            break;
          default:
            break;
        }
      } catch (err) {
        const errMessage = err instanceof AggregateError
          ? err.errors.reduce(
            (acc, current) => (acc.length === 0 ? current.message : `${acc}, ${current.message}`),
            '',
          )
          : err.message;

        Logger.logSignaling(
          __FILE__,
          __METHOD__,
          `Attempt ${attemptsCount} failed: ${errMessage}`,
          LogLevel.Warning,
        );
      }
    }

    if (!callStatusResponse && apiCallStatus === 'INVITED') {
      // The caller's device may have been disconnected, but if the call is still INVITED
      // at the API side, it's safe to send 'receiverDeclined';
      return false;
    }

    throw new Error('Failed to reliably determine if the call was answered');
  }

  setAudioOutputSink = (deviceId) => {
    const { audioInput } = this.props;
    audioInput.setSinkId && audioInput.setSinkId === 'function' && audioInput.setSinkId(deviceId);
  }

  resetToIdleState = () => {
    this.callState = CallState.IDLE;
  }
}

export default Connection;



// WEBPACK FOOTER //
// ./utils/connection.js